import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from './config';
import './PMCC.css';

function PMCC({ username, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pmccs, setPmccs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('list'); // 'list', 'create', 'edit', 'detail'
  const [selectedPmcc, setSelectedPmcc] = useState(null);
  const [leapsTrades, setLeapsTrades] = useState([]);
  const [selectedLeapsIds, setSelectedLeapsIds] = useState([]);
  const [description, setDescription] = useState('');
  const [tradePlan, setTradePlan] = useState('');
  const [selectedCoveredCallsIds, setSelectedCoveredCallsIds] = useState([]);
  const [possibleCoveredCalls, setPossibleCoveredCalls] = useState([]);
  const [showClosedLeaps, setShowClosedLeaps] = useState(false);
  const [showAddLeapsModal, setShowAddLeapsModal] = useState(false);
  const [showAddCoveredCallsModal, setShowAddCoveredCallsModal] = useState(false);
  const [leapsSortConfig, setLeapsSortConfig] = useState({ key: 'trade_date', direction: 'desc' });
  const [coveredCallsSortConfig, setCoveredCallsSortConfig] = useState({ key: 'trade_date', direction: 'desc' });
  const [availableLeapsForAdd, setAvailableLeapsForAdd] = useState([]);
  const [selectedLeapsToAdd, setSelectedLeapsToAdd] = useState([]);
  const [availableCoveredCallsForAdd, setAvailableCoveredCallsForAdd] = useState([]);
  const [selectedCoveredCallsToAdd, setSelectedCoveredCallsToAdd] = useState([]);
  const [pmccDetail, setPmccDetail] = useState(null);
  const [leapsDetails, setLeapsDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [expandedLeaps, setExpandedLeaps] = useState(new Set());
  const [conidTrades, setConidTrades] = useState({}); // Cache for all trades per CONID
  const [openPositions, setOpenPositions] = useState([]); // Cache for open positions to get current prices
  const [showClosePmccDialog, setShowClosePmccDialog] = useState(false);
  const [pmccToClose, setPmccToClose] = useState(null);

  useEffect(() => {
    fetchPMCCs();
    fetchLeapsTrades();
  }, []);

  // Load PMCC detail from URL if ID is present
  useEffect(() => {
    if (id) {
      const pmccId = parseInt(id, 10);
      if (!isNaN(pmccId)) {
        // Only load if we don't already have this PMCC detail loaded
        if (!pmccDetail || pmccDetail.id !== pmccId) {
          // Find PMCC in the list or fetch it
          const pmcc = pmccs.find(p => p.id === pmccId);
          if (pmcc) {
            handleViewDetail(pmcc);
          } else if (pmccs.length > 0) {
            // PMCC not found in list, try to fetch it directly
            handleViewDetail({ id: pmccId });
          }
        }
      }
    } else {
      // No ID in URL, make sure we're on list view
      if (view === 'detail') {
        setView('list');
        setPmccDetail(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, pmccs]);

  useEffect(() => {
    if (showClosedLeaps) {
      fetchLeapsTrades();
    } else {
      fetchLeapsTrades();
    }
  }, [showClosedLeaps]);

  // Fetch possible covered calls when LEAPS are selected (create view)
  useEffect(() => {
    if (selectedLeapsIds.length > 0 && view === 'create') {
      fetchPossibleCoveredCalls();
    } else {
      setPossibleCoveredCalls([]);
      setSelectedCoveredCallsIds([]);
    }
  }, [selectedLeapsIds, view]);

  // Fetch possible covered calls when editing a PMCC
  useEffect(() => {
    if (view === 'edit' && selectedPmcc) {
      fetchPossibleCoveredCallsForEdit(selectedPmcc);
    }
  }, [view, selectedPmcc]);

  const fetchPMCCs = async () => {
    try {
      setLoading(true);
      setError(null);
      // Add timeout to prevent hanging
      const response = await axios.get(`${API_URL}/portfolio/pmcc/?page_size=all`, {
        timeout: 30000 // 30 second timeout
      });
      const data = response.data.results || response.data;
      const allPmccs = Array.isArray(data) ? data : [];
      setPmccs(allPmccs);
    } catch (err) {
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        setError('Request timed out. The server may be processing a large amount of data. Please try again.');
      } else {
        setError('Error fetching PMCCs: ' + err.message);
      }
      console.error('PMCC fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeapsTrades = async () => {
    try {
      // Fetch all call option trades (BUY to open)
      // LEAPS must be: BUY + CALL + OPEN indicator
      const tradesResponse = await axios.get(`${API_URL}/portfolio/trades/?page_size=all&asset_category=OPT&put_call=C&buy_sell=BUY`);
      const allTrades = tradesResponse.data.results || tradesResponse.data;
      const tradesArray = Array.isArray(allTrades) ? allTrades : [];
      
      // Also fetch SELL trades to find closing trades for closed LEAPS
      const sellTradesResponse = await axios.get(`${API_URL}/portfolio/trades/?page_size=all&asset_category=OPT&put_call=C&buy_sell=SELL`);
      const allSellTrades = sellTradesResponse.data.results || sellTradesResponse.data;
      const sellTradesArray = Array.isArray(allSellTrades) ? allSellTrades : [];
      
      // Combine all trades for finding closing trades
      const allTradesForClosing = [...tradesArray, ...sellTradesArray];
      
      // Filter to only opening trades (open_close_indicator = 'O' or 'OPEN')
      const openingTrades = tradesArray.filter(trade => {
        const indicator = (trade.open_close_indicator || '').toUpperCase();
        return indicator === 'O' || indicator === 'OPEN';
      });

      // Fetch open positions to check which trades are still open
      const positionsResponse = await axios.get(`${API_URL}/portfolio/open-positions/?page_size=all&asset_category=OPT&put_call=C`);
      const allPositions = positionsResponse.data.results || positionsResponse.data;
      const positionsArray = Array.isArray(allPositions) ? allPositions : [];

      // Create a map of open positions by conid and account_id
      // A position is open if position > 0 (long position)
      const openPositionsMap = new Map();
      positionsArray.forEach(pos => {
        if (parseFloat(pos.position) > 0) {
          const key = `${pos.conid}_${pos.account_id || pos.account}`;
          openPositionsMap.set(key, pos);
        }
      });

      // Filter for LEAPS: 
      // 1. Must be opening trade (BUY + CALL + OPEN indicator) - already filtered above
      // 2. Must have expiry and at least 360 days from trade_date to expiry
      // 3. Must still be open (have a corresponding open position) - unless showClosedLeaps is true
      const leaps = openingTrades.filter(trade => {
        // Check if trade is still open (unless showing closed LEAPS)
        if (!showClosedLeaps) {
          const positionKey = `${trade.conid}_${trade.account_id || trade.account}`;
          if (!openPositionsMap.has(positionKey)) {
            return false; // Position is closed
          }
        }

        // Check LEAPS criteria (360+ days from trade_date to expiry)
        if (!trade.expiry) return false;
        
        // Parse trade date and expiry
        const tradeDate = parseDate(trade.trade_date);
        const expiryDate = parseDate(trade.expiry);
        
        if (!tradeDate || !expiryDate) return false;
        
        // Calculate days until expiry at time of purchase
        const daysUntilExpiry = Math.floor((expiryDate - tradeDate) / (1000 * 60 * 60 * 24));
        
        return daysUntilExpiry >= 360;
      });

      // For closed LEAPS, find closing trade dates
      const leapsWithCloseDate = leaps.map(leapsTrade => {
        // Check if this LEAPS is closed
        const positionKey = `${leapsTrade.conid}_${leapsTrade.account_id || leapsTrade.account}`;
        const isOpen = openPositionsMap.has(positionKey);
        
        if (!isOpen && leapsTrade.conid) {
          // Find closing trade (SELL with same conid and later date)
          const closingTrade = allTradesForClosing.find(t => 
            t.conid === leapsTrade.conid &&
            t.id !== leapsTrade.id &&
            t.buy_sell === 'SELL' &&
            parseDate(t.trade_date) >= parseDate(leapsTrade.trade_date)
          );
          
          if (closingTrade) {
            console.log('Found closing trade for LEAPS:', {
              leapsId: leapsTrade.id,
              leapsSymbol: leapsTrade.symbol,
              closingTradeId: closingTrade.id,
              closingTradeDate: closingTrade.trade_date
            });
            return {
              ...leapsTrade,
              close_date: closingTrade.trade_date,
              is_closed: true
            };
          } else {
            console.warn('No closing trade found for closed LEAPS:', {
              leapsId: leapsTrade.id,
              leapsSymbol: leapsTrade.symbol,
              conid: leapsTrade.conid,
              account: leapsTrade.account_id || leapsTrade.account
            });
          }
        }
        
        return {
          ...leapsTrade,
          close_date: null,
          is_closed: false
        };
      });

      setLeapsTrades(leapsWithCloseDate);
    } catch (err) {
      console.error('Error fetching LEAPS trades:', err);
      setLeapsTrades([]);
    }
  };

  const parseDate = (dateString) => {
    if (!dateString) return null;
    
    // Try DD/MM/YY format (e.g., "17/10/25")
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const year = 2000 + parseInt(parts[2], 10);
      const date = new Date(year, month, day);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    
    // Try DD Mon YYYY format (e.g., "17 oct 2025")
    const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const dateParts = dateString.toLowerCase().trim().split(/\s+/);
    if (dateParts.length === 3) {
      const day = parseInt(dateParts[0], 10);
      const monthIndex = monthNames.indexOf(dateParts[1]);
      const year = parseInt(dateParts[2], 10);
      if (monthIndex !== -1 && !isNaN(day) && !isNaN(year)) {
        const date = new Date(year, monthIndex, day);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    }
    
    // Try ISO format
    const isoDate = new Date(dateString);
    if (!isNaN(isoDate.getTime())) {
      return isoDate;
    }
    
    return null;
  };

  const handleLeapsSort = (key) => {
    let direction = 'asc';
    if (leapsSortConfig.key === key && leapsSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setLeapsSortConfig({ key, direction });
  };

  const handleCoveredCallsSort = (key) => {
    let direction = 'asc';
    if (coveredCallsSortConfig.key === key && coveredCallsSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setCoveredCallsSortConfig({ key, direction });
  };

  const getLeapsSortIcon = (key) => {
    if (leapsSortConfig.key !== key) {
      return '↕️';
    }
    return leapsSortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const getCoveredCallsSortIcon = (key) => {
    if (coveredCallsSortConfig.key !== key) {
      return '↕️';
    }
    return coveredCallsSortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const sortLeaps = (leaps) => {
    if (!leapsSortConfig.key) return leaps;
    
    const sorted = [...leaps].sort((a, b) => {
      let aVal, bVal;
      
      switch (leapsSortConfig.key) {
        case 'symbol':
          aVal = a.symbol || '';
          bVal = b.symbol || '';
          break;
        case 'underlying':
          aVal = a.underlying_symbol || '';
          bVal = b.underlying_symbol || '';
          break;
        case 'trade_date':
          aVal = parseDate(a.trade_date);
          bVal = parseDate(b.trade_date);
          break;
        case 'expiry':
          aVal = parseDate(a.expiry);
          bVal = parseDate(b.expiry);
          break;
        case 'close_date':
          aVal = a.close_date ? parseDate(a.close_date) : null;
          bVal = b.close_date ? parseDate(b.close_date) : null;
          break;
        case 'conid':
          aVal = a.conid || '';
          bVal = b.conid || '';
          break;
        default:
          return 0;
      }
      
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      if (aVal < bVal) return leapsSortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return leapsSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };

  const sortCoveredCalls = (calls) => {
    if (!coveredCallsSortConfig.key) return calls;
    
    const sorted = [...calls].sort((a, b) => {
      let aVal, bVal;
      
      switch (coveredCallsSortConfig.key) {
        case 'symbol':
          aVal = a.symbol || '';
          bVal = b.symbol || '';
          break;
        case 'trade_date':
          aVal = parseDate(a.trade_date);
          bVal = parseDate(b.trade_date);
          break;
        case 'expiry':
          aVal = parseDate(a.expiry);
          bVal = parseDate(b.expiry);
          break;
        case 'premium':
          aVal = a.trade_money ? Math.abs(parseFloat(a.trade_money)) : (a.trade_price ? parseFloat(a.trade_price) : 0);
          bVal = b.trade_money ? Math.abs(parseFloat(b.trade_money)) : (b.trade_price ? parseFloat(b.trade_price) : 0);
          break;
        case 'conid':
          aVal = a.conid || '';
          bVal = b.conid || '';
          break;
        default:
          return 0;
      }
      
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      if (aVal < bVal) return coveredCallsSortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return coveredCallsSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };

  const fetchPossibleCoveredCalls = async () => {
    if (selectedLeapsIds.length === 0) {
      setPossibleCoveredCalls([]);
      return;
    }

    try {
      // Get selected LEAPS trades to determine underlying symbol and date range
      const selectedLeaps = leapsTrades.filter(t => selectedLeapsIds.includes(t.id));
      if (selectedLeaps.length === 0) {
        setPossibleCoveredCalls([]);
        return;
      }

      // Get underlying symbol (should be same for all LEAPS - enforced by selection logic)
      const underlyingSymbol = selectedLeaps[0].underlying_symbol || 
        (selectedLeaps[0].symbol ? selectedLeaps[0].symbol.split()[0] : null);
      
      if (!underlyingSymbol) {
        console.warn('No underlying symbol found for selected LEAPS:', selectedLeaps[0]);
        setPossibleCoveredCalls([]);
        return;
      }
      
      console.log('Fetching covered calls for underlying symbol:', underlyingSymbol);

      // Get all PMCCs to check which covered calls are already used
      const allPmccsResponse = await axios.get(`${API_URL}/portfolio/pmcc/?page_size=all`);
      const allPmccsData = allPmccsResponse.data.results || allPmccsResponse.data;
      const allPmccs = Array.isArray(allPmccsData) ? allPmccsData : [];
      
      // Collect all trade IDs that are already used in PMCCs (for covered calls)
      const usedCoveredCallIds = new Set();
      allPmccs.forEach(pmcc => {
        // Get covered calls from trades (if using new model) or from tags
        if (pmcc.trades && Array.isArray(pmcc.trades)) {
          pmcc.trades.forEach(pmccTrade => {
            if (pmccTrade.trade_type === 'COVERED_CALL' && pmccTrade.trade) {
              usedCoveredCallIds.add(pmccTrade.trade);
            }
          });
        }
      });

      // Calculate date range: earliest LEAPS entry date to latest LEAPS close date (or expiry if not closed)
      let earliestEntryDate = null;
      let latestCloseDate = null;

      selectedLeaps.forEach(leapsTrade => {
        const entryDate = parseDate(leapsTrade.trade_date);
        if (!entryDate) {
          console.warn('LEAPS trade has no valid entry date:', leapsTrade.id, leapsTrade.symbol, leapsTrade.trade_date);
          return; // Skip this LEAPS if we can't parse the entry date
        }
        
        // Update earliest entry date
        if (!earliestEntryDate || entryDate < earliestEntryDate) {
          earliestEntryDate = entryDate;
        }
        
        // For closed LEAPS: try to find closing trade date
        // For open LEAPS: use expiry date
        let closeDate = null;
        
        // First, try to find closing trade if we have conid
        if (leapsTrade.conid) {
          // Fetch trades for this specific conid to find closing trade
          // We'll do this in a simpler way - just use expiry for now if we can't easily find close date
          // The backend will handle the proper filtering
        }
        
        // Use expiry date (will be replaced by close date if LEAPS is closed, handled by backend)
        closeDate = parseDate(leapsTrade.expiry);
        
        if (closeDate && (!latestCloseDate || closeDate > latestCloseDate)) {
          latestCloseDate = closeDate;
        } else if (!closeDate) {
          console.warn('LEAPS trade has no valid expiry date:', leapsTrade.id, leapsTrade.symbol, leapsTrade.expiry);
        }
      });

      // Debug logging
      console.log('Date range for covered calls:', {
        earliestEntryDate: earliestEntryDate ? new Date(earliestEntryDate).toISOString() : null,
        latestCloseDate: latestCloseDate ? new Date(latestCloseDate).toISOString() : null
      });

      if (!earliestEntryDate || !latestCloseDate) {
        console.warn('Missing date range for covered calls filtering');
        setPossibleCoveredCalls([]);
        return;
      }

      // Fetch all CALL SELL trades with same underlying symbol
      // Covered calls must be: SELL + CALL + OPEN indicator
      // URL encode the underlying symbol to handle special characters
      const encodedSymbol = encodeURIComponent(underlyingSymbol);
      const url = `${API_URL}/portfolio/trades/?page_size=all&put_call=C&buy_sell=SELL&underlying_symbol=${encodedSymbol}`;
      console.log('Fetching covered calls from:', url);
      
      const response = await axios.get(url);
      const allTrades = response.data.results || response.data;
      const tradesArray = Array.isArray(allTrades) ? allTrades : [];
      console.log(`Found ${tradesArray.length} total SELL CALL trades for ${underlyingSymbol}`);

      // Filter to only opening trades (open_close_indicator = 'O' or 'OPEN')
      const openingSellTrades = tradesArray.filter(trade => {
        const indicator = (trade.open_close_indicator || '').toUpperCase();
        return indicator === 'O' || indicator === 'OPEN';
      });

      // Filter by date range (but include already used covered calls so they can be shown as disabled)
      // IMPORTANT: Covered calls must have trade_date >= earliest LEAPS entry date
      // AND trade_date <= latest LEAPS close date (or expiry if not closed)
      const possibleCalls = openingSellTrades.filter(trade => {
        const tradeDate = parseDate(trade.trade_date);
        if (!tradeDate) {
          console.warn('Trade has no valid trade_date:', trade.id, trade.symbol, trade.trade_date);
          return false;
        }
        
        // Covered call trade_date must be >= earliest LEAPS entry date
        // AND <= latest LEAPS close date (or expiry)
        const isAfterEntry = tradeDate >= earliestEntryDate;
        const isBeforeClose = tradeDate <= latestCloseDate;
        const isInRange = isAfterEntry && isBeforeClose;
        
        if (!isInRange) {
          console.debug('Trade outside date range:', {
            tradeId: trade.id,
            symbol: trade.symbol,
            tradeDate: new Date(tradeDate).toISOString(),
            tradeDateFormatted: trade.trade_date,
            earliestEntry: new Date(earliestEntryDate).toISOString(),
            latestClose: new Date(latestCloseDate).toISOString(),
            isAfterEntry,
            isBeforeClose
          });
        }
        return isInRange;
      });

      console.log('Covered calls filtering result:', {
        totalOpeningSellTrades: openingSellTrades.length,
        possibleCalls: possibleCalls.length,
        dateRange: {
          from: new Date(earliestEntryDate).toISOString(),
          to: new Date(latestCloseDate).toISOString()
        }
      });

      setPossibleCoveredCalls(possibleCalls);
    } catch (err) {
      console.error('Error fetching possible covered calls:', err);
      if (err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response data:', err.response.data);
      }
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        url: err.config?.url
      });
      setPossibleCoveredCalls([]);
    }
  };

  const handleCreate = async () => {
    if (selectedLeapsIds.length === 0) {
      setError('Please select at least one LEAPS trade');
      return;
    }

    try {
      setError(null);
      console.log('Creating PMCC with trade IDs:', selectedLeapsIds);
      // Build request data, only include description and trade_plan if they have values
      const requestData = {
        leaps_trade_ids: selectedLeapsIds,
        covered_calls_trade_ids: selectedCoveredCallsIds.length > 0 ? selectedCoveredCallsIds : []
      };
      
      // Only include description and trade_plan if they have non-empty values
      if (description && description.trim()) {
        requestData.description = description.trim();
      }
      if (tradePlan && tradePlan.trim()) {
        requestData.trade_plan = tradePlan.trim();
      }
      
      const response = await axios.post(`${API_URL}/portfolio/pmcc/`, requestData);
      console.log('PMCC created successfully:', response.data);
      await fetchPMCCs();
      navigate('/pmcc');
      setView('list');
      setSelectedLeapsIds([]);
      setSelectedCoveredCallsIds([]);
      setDescription('');
      setTradePlan('');
      setPossibleCoveredCalls([]);
    } catch (err) {
      console.error('Error creating PMCC:', err);
      console.error('Response status:', err.response?.status);
      console.error('Response data:', err.response?.data);
      console.error('Full error response:', JSON.stringify(err.response?.data, null, 2));
      // Log all field errors
      if (err.response?.data) {
        console.error('Field errors:', Object.keys(err.response.data));
        for (const [field, errors] of Object.entries(err.response.data)) {
          console.error(`  ${field}:`, errors);
        }
      }
      let errorMessage = 'Unknown error';
      
      if (err.response?.data) {
        const data = err.response.data;
        // Try to extract error message from various possible formats
        if (data.detail) {
          errorMessage = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        } else if (data.non_field_errors) {
          errorMessage = Array.isArray(data.non_field_errors) 
            ? data.non_field_errors.join(', ') 
            : String(data.non_field_errors);
        } else {
          // Build error message from all field errors
          const fieldErrors = [];
          for (const [field, errors] of Object.entries(data)) {
            if (Array.isArray(errors)) {
              fieldErrors.push(`${field}: ${errors.join(', ')}`);
            } else {
              fieldErrors.push(`${field}: ${errors}`);
            }
          }
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join(' | ');
          } else {
            errorMessage = JSON.stringify(data);
          }
        }
      } else {
        errorMessage = err.message;
      }
      
      setError('Error creating PMCC: ' + errorMessage);
    }
  };

  const handleEdit = async (pmcc) => {
    try {
      // Fetch full PMCC data including trades
      const [pmccResponse, tradesResponse] = await Promise.all([
        axios.get(`${API_URL}/portfolio/pmcc/${pmcc.id}/`),
        axios.get(`${API_URL}/portfolio/pmcc/${pmcc.id}/all_trades/`)
      ]);
      
      const pmccData = pmccResponse.data;
      const { trades_by_conid } = tradesResponse.data;
      
      setSelectedPmcc(pmccData);
      setView('edit');
      
      // Check if PMCC is open and has no open LEAPS
      console.log('handleEdit: PMCC is_closed:', pmccData.is_closed);
      console.log('handleEdit: PMCC leaps count:', pmccData.leaps?.length || 0);
      if (!pmccData.is_closed) {
        // If no LEAPS at all, show dialog
        if (!pmccData.leaps || pmccData.leaps.length === 0) {
          console.log('handleEdit: No LEAPS found, showing close dialog');
          setPmccToClose(pmccData);
          setShowClosePmccDialog(true);
        } else {
          const hasOpenLeaps = checkOpenLeaps(pmccData, trades_by_conid);
          console.log('handleEdit: hasOpenLeaps:', hasOpenLeaps);
          if (!hasOpenLeaps) {
            console.log('handleEdit: All LEAPS closed, showing close dialog');
            setPmccToClose(pmccData);
            setShowClosePmccDialog(true);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching PMCC details:', err);
      setError('Error loading PMCC details: ' + (err.response?.data?.detail || err.message));
      // Fallback to using the pmcc data we have
      setSelectedPmcc(pmcc);
      setView('edit');
    }
  };

  const fetchAvailableLeapsForAdd = async (underlyingSymbol) => {
    try {
      // Fetch all call option trades (BUY to open)
      // LEAPS must be: BUY + CALL + OPEN indicator
      const tradesResponse = await axios.get(`${API_URL}/portfolio/trades/?page_size=all&asset_category=OPT&put_call=C&buy_sell=BUY`);
      const allTrades = tradesResponse.data.results || tradesResponse.data;
      const tradesArray = Array.isArray(allTrades) ? allTrades : [];
      
      // Filter to only opening trades (open_close_indicator = 'O' or 'OPEN')
      const openingTrades = tradesArray.filter(trade => {
        const indicator = (trade.open_close_indicator || '').toUpperCase();
        return indicator === 'O' || indicator === 'OPEN';
      });

      // Fetch open positions to check which trades are still open
      const positionsResponse = await axios.get(`${API_URL}/portfolio/open-positions/?page_size=all&asset_category=OPT&put_call=C`);
      const allPositions = positionsResponse.data.results || positionsResponse.data;
      const positionsArray = Array.isArray(allPositions) ? allPositions : [];

      // Create a map of open positions by conid and account_id
      // A position is open if position > 0 (long position)
      const openPositionsMap = new Map();
      positionsArray.forEach(pos => {
        if (parseFloat(pos.position) > 0) {
          const key = `${pos.conid}_${pos.account_id || pos.account}`;
          openPositionsMap.set(key, pos);
        }
      });

      // Get all PMCCs to find which LEAPS are already used
      const pmccResponse = await axios.get(`${API_URL}/portfolio/pmcc/?page_size=all`);
      const pmccData = pmccResponse.data.results || pmccResponse.data;
      const allPmccs = Array.isArray(pmccData) ? pmccData : [];

      // Collect all trade IDs that are already used in PMCCs
      const usedTradeIds = new Set();
      allPmccs.forEach(pmcc => {
        if (pmcc.leaps && Array.isArray(pmcc.leaps)) {
          pmcc.leaps.forEach(leaps => {
            if (leaps.trade) {
              usedTradeIds.add(leaps.trade);
            }
          });
        }
      });

      // Filter for LEAPS: 
      // 1. Must be opening trade (BUY + CALL + OPEN indicator) - already filtered above
      // 2. Must still be open (have a corresponding open position)
      // 3. Must have expiry and at least 360 days from trade_date to expiry
      // 4. Must match the underlying symbol
      // 5. Must not be already used in any PMCC
      const availableLeaps = openingTrades.filter(trade => {
        // Check if trade is still open
        const positionKey = `${trade.conid}_${trade.account_id || trade.account}`;
        if (!openPositionsMap.has(positionKey)) {
          return false; // Position is closed
        }

        // Check underlying symbol match
        const tradeUnderlying = trade.underlying_symbol || (trade.symbol ? trade.symbol.split()[0] : null);
        if (!tradeUnderlying || tradeUnderlying.toUpperCase() !== underlyingSymbol.toUpperCase()) {
          return false;
        }

        // Check if already used
        if (usedTradeIds.has(trade.id)) {
          return false;
        }

        // Check LEAPS criteria (360+ days from trade_date to expiry)
        if (!trade.expiry) return false;
        
        const tradeDate = parseDate(trade.trade_date);
        const expiryDate = parseDate(trade.expiry);
        
        if (!tradeDate || !expiryDate) return false;
        
        const daysUntilExpiry = Math.floor((expiryDate - tradeDate) / (1000 * 60 * 60 * 24));
        
        return daysUntilExpiry >= 360;
      });

      setAvailableLeapsForAdd(availableLeaps);
    } catch (err) {
      console.error('Error fetching available LEAPS:', err);
      setAvailableLeapsForAdd([]);
    }
  };

  const openAddLeapsModal = () => {
    if (selectedPmcc) {
      fetchAvailableLeapsForAdd(selectedPmcc.underlying_symbol);
      setShowAddLeapsModal(true);
      setSelectedLeapsToAdd([]);
    }
  };

  const handleAddLeaps = async () => {
    if (selectedLeapsToAdd.length === 0) {
      setError('Please select at least one LEAPS trade to add');
      return;
    }

    try {
      setError(null);
      // Get current LEAPS IDs
      const currentLeapsIds = selectedPmcc.leaps.map(l => l.trade);
      // Add new LEAPS IDs
      const allLeapsIds = [...currentLeapsIds, ...selectedLeapsToAdd];
      
      await axios.patch(`${API_URL}/portfolio/pmcc/${selectedPmcc.id}/`, {
        leaps_trade_ids: allLeapsIds
      });
      await fetchPMCCs();
      // Refresh selected PMCC
      const response = await axios.get(`${API_URL}/portfolio/pmcc/${selectedPmcc.id}/`);
      setSelectedPmcc(response.data);
      setShowAddLeapsModal(false);
      setSelectedLeapsToAdd([]);
      await fetchAvailableLeapsForAdd(selectedPmcc.underlying_symbol);
    } catch (err) {
      console.error('Error adding LEAPS:', err);
      console.error('Response data:', err.response?.data);
      let errorMessage = 'Unknown error';
      
      if (err.response?.data) {
        const data = err.response.data;
        if (data.detail) {
          errorMessage = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        } else if (data.leaps_trade_ids) {
          errorMessage = Array.isArray(data.leaps_trade_ids) 
            ? data.leaps_trade_ids.join(', ') 
            : String(data.leaps_trade_ids);
        } else {
          const firstKey = Object.keys(data)[0];
          const firstValue = data[firstKey];
          if (firstValue) {
            errorMessage = Array.isArray(firstValue) ? firstValue[0] : String(firstValue);
          } else {
            errorMessage = JSON.stringify(data);
          }
        }
      } else {
        errorMessage = err.message;
      }
      
      setError('Error adding LEAPS: ' + errorMessage);
    }
  };

  const fetchPossibleCoveredCallsForEdit = async (pmcc) => {
    if (!pmcc || !pmcc.leaps || pmcc.leaps.length === 0) {
      setAvailableCoveredCallsForAdd([]);
      return;
    }

    try {
      const underlyingSymbol = pmcc.underlying_symbol;
      if (!underlyingSymbol) {
        setAvailableCoveredCallsForAdd([]);
        return;
      }

      // Get all PMCCs to check which covered calls are already used
      const allPmccsResponse = await axios.get(`${API_URL}/portfolio/pmcc/?page_size=all`);
      const allPmccsData = allPmccsResponse.data.results || allPmccsResponse.data;
      const allPmccs = Array.isArray(allPmccsData) ? allPmccsData : [];
      
      // Collect all trade IDs that are already used in PMCCs (for covered calls)
      const usedCoveredCallIds = new Set();
      allPmccs.forEach(p => {
        if (p.trades && Array.isArray(p.trades)) {
          p.trades.forEach(pmccTrade => {
            if (pmccTrade.trade_type === 'COVERED_CALL' && pmccTrade.trade) {
              usedCoveredCallIds.add(pmccTrade.trade);
            }
          });
        }
      });

      // Get date range from LEAPS
      const parseDate = (dateStr) => {
        if (!dateStr) return null;
        try {
          if (dateStr.includes('/')) {
            const parts = dateStr.split('/');
            if (parts.length === 3) {
              return new Date(2000 + parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
            }
          }
          return new Date(dateStr);
        } catch {
          return null;
        }
      };

      let earliestTradeDate = null;
      let latestExpiryDate = null;
      
      pmcc.leaps.forEach(leaps => {
        const tradeDate = parseDate(leaps.trade_date);
        const expiryDate = parseDate(leaps.trade_expiry);
        if (tradeDate && (!earliestTradeDate || tradeDate < earliestTradeDate)) {
          earliestTradeDate = tradeDate;
        }
        if (expiryDate && (!latestExpiryDate || expiryDate > latestExpiryDate)) {
          latestExpiryDate = expiryDate;
        }
      });

      if (!earliestTradeDate || !latestExpiryDate) {
        setAvailableCoveredCallsForAdd([]);
        return;
      }

      // Fetch all CALL SELL trades with same underlying symbol
      const response = await axios.get(`${API_URL}/portfolio/trades/?page_size=all&put_call=C&buy_sell=SELL&underlying_symbol=${underlyingSymbol}`);
      const allTrades = response.data.results || response.data;
      const tradesArray = Array.isArray(allTrades) ? allTrades : [];

      // Filter to only opening trades (open_close_indicator = 'O' or 'OPEN')
      const openingSellTrades = tradesArray.filter(trade => {
        const indicator = (trade.open_close_indicator || '').toUpperCase();
        return indicator === 'O' || indicator === 'OPEN';
      });

      // Filter by date range (include already used covered calls so they can be shown as disabled)
      const possibleCalls = openingSellTrades.filter(trade => {
        const tradeDate = parseDate(trade.trade_date);
        if (!tradeDate) return false;
        return tradeDate >= earliestTradeDate && tradeDate <= latestExpiryDate;
      });

      setAvailableCoveredCallsForAdd(possibleCalls);
    } catch (err) {
      console.error('Error fetching possible covered calls for edit:', err);
      setAvailableCoveredCallsForAdd([]);
    }
  };

  const openAddCoveredCallsModal = () => {
    if (selectedPmcc) {
      fetchPossibleCoveredCallsForEdit(selectedPmcc);
      setShowAddCoveredCallsModal(true);
      setSelectedCoveredCallsToAdd([]);
    }
  };

  const handleAddCoveredCalls = async () => {
    if (selectedCoveredCallsToAdd.length === 0) {
      setError('Please select at least one covered call to add');
      return;
    }

    try {
      setError(null);
      // Get current covered calls IDs
      const currentCoveredCallsIds = (selectedPmcc.trades || [])
        .filter(t => t.trade_type === 'COVERED_CALL')
        .map(t => t.trade);
      // Add new covered calls IDs
      const allCoveredCallsIds = [...currentCoveredCallsIds, ...selectedCoveredCallsToAdd];
      
      await axios.patch(`${API_URL}/portfolio/pmcc/${selectedPmcc.id}/`, {
        covered_calls_trade_ids: allCoveredCallsIds
      });
      await fetchPMCCs();
      // Refresh selected PMCC
      const response = await axios.get(`${API_URL}/portfolio/pmcc/${selectedPmcc.id}/`);
      setSelectedPmcc(response.data);
      setShowAddCoveredCallsModal(false);
      setSelectedCoveredCallsToAdd([]);
      await fetchPossibleCoveredCallsForEdit(response.data);
    } catch (err) {
      console.error('Error adding covered calls:', err);
      console.error('Response data:', err.response?.data);
      let errorMessage = 'Unknown error occurred while adding covered calls.';
      
      if (err.response?.data) {
        const data = err.response.data;
        
        // Check for covered_calls_trade_ids error (most common)
        if (data.covered_calls_trade_ids) {
          const error = data.covered_calls_trade_ids;
          if (Array.isArray(error)) {
            errorMessage = error.join(' ');
          } else {
            errorMessage = String(error);
          }
        } 
        // Check for detail error
        else if (data.detail) {
          errorMessage = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        } 
        // Check for other field errors
        else {
          const firstKey = Object.keys(data)[0];
          const firstValue = data[firstKey];
          if (firstValue) {
            if (Array.isArray(firstValue)) {
              errorMessage = firstValue[0];
            } else {
              errorMessage = String(firstValue);
            }
          } else {
            errorMessage = JSON.stringify(data);
          }
        }
      } else {
        errorMessage = err.message || 'Network error occurred. Please check your connection.';
      }
      
      // Show error in a popup/alert
      alert(`Cannot Add Covered Calls\n\n${errorMessage}\n\nPlease check the requirements:\n- PMCC must have at least one open LEAPS position\n- Covered calls must be SELL + CALL + OPEN indicator\n- Covered calls must match the PMCC underlying symbol`);
      setError(errorMessage);
    }
  };

  const handleDelete = async (pmcc) => {
    if (!window.confirm(`Are you sure you want to delete PMCC "${pmcc.name}"?`)) {
      return;
    }

    try {
      setError(null);
      await axios.delete(`${API_URL}/portfolio/pmcc/${pmcc.id}/`);
      await fetchPMCCs();
    } catch (err) {
      setError('Error deleting PMCC: ' + (err.response?.data?.detail || err.message));
      console.error(err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    // Handle ISO format dates (e.g., "2025-12-25T15:19:32.787428+01:00")
    // Or DD/MM/YY format (e.g., "17/10/25")
    // Or DD Mon YYYY format (e.g., "17 oct 2025")
    try {
      let date;
      
      // Try DD/MM/YY format first
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const year = 2000 + parseInt(parts[2], 10);
        date = new Date(year, month, day);
      } else {
        // Try ISO format or other formats
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) return dateString; // Invalid date, return original
      
      const day = date.getDate();
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[date.getMonth()];
      const year = date.getFullYear() % 100; // Get last 2 digits of year
      
      return `${day} ${month} ${year}`;
    } catch (e) {
      return dateString; // Return original if parsing fails
    }
  };

  const formatCurrency = (value) => {
    if (!value || value === '0.00' || value === 0) return '€ 0.00';
    const num = parseFloat(value);
    const formattedNumber = num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `€ ${formattedNumber}`;
  };

  const fetchLeapsDetails = async (leaps) => {
    try {
      // Fetch the opening trade
      const openingTradeResponse = await axios.get(`${API_URL}/portfolio/trades/${leaps.trade}/`);
      const openingTrade = openingTradeResponse.data;

      // Find closing trade (SELL with same CONID and later date)
      const allTradesResponse = await axios.get(`${API_URL}/portfolio/trades/?page_size=all&conid=${openingTrade.conid}&account=${openingTrade.account}`);
      const allTrades = allTradesResponse.data.results || allTradesResponse.data;
      const tradesArray = Array.isArray(allTrades) ? allTrades : [];
      
      const closingTrade = tradesArray.find(t => 
        t.id !== openingTrade.id && 
        t.buy_sell === 'SELL' &&
        parseDate(t.trade_date) >= parseDate(openingTrade.trade_date)
      );

      const entryDate = parseDate(openingTrade.trade_date);
      const exitDate = closingTrade ? parseDate(closingTrade.trade_date) : null;
      const daysOpen = entryDate && exitDate ? Math.floor((exitDate - entryDate) / (1000 * 60 * 60 * 24)) : null;

      return {
        openingTrade,
        closingTrade,
        entryDate,
        exitDate,
        daysOpen,
        entryPrice: openingTrade.trade_price,
        exitPrice: closingTrade ? closingTrade.trade_price : null,
        pnl: closingTrade ? closingTrade.fifo_pnl_realized : (openingTrade.mtm_pnl || null),
        isClosed: !!closingTrade
      };
    } catch (err) {
      console.error('Error fetching LEAPS details:', err);
      return null;
    }
  };

  const handleViewDetail = async (pmcc) => {
    try {
      setLoadingDetails(true);
      setExpandedLeaps(new Set()); // Reset expanded state
      setConidTrades({}); // Reset CONID trades cache
      
      // Navigate to URL with PMCC ID (only if not already on that URL)
      if (!id || parseInt(id, 10) !== pmcc.id) {
        navigate(`/pmcc/${pmcc.id}`, { replace: true });
      }
      
      // Fetch PMCC details, all trades, and open positions in parallel (3 calls total)
      const [pmccResponse, tradesResponse, positionsResponse] = await Promise.all([
        axios.get(`${API_URL}/portfolio/pmcc/${pmcc.id}/`),
        axios.get(`${API_URL}/portfolio/pmcc/${pmcc.id}/all_trades/`),
        axios.get(`${API_URL}/portfolio/open-positions/?page_size=all&asset_category=OPT&put_call=C`)
      ]);
      
      const pmccData = pmccResponse.data;
      const { trades_by_conid } = tradesResponse.data;
      const positionsData = positionsResponse.data.results || positionsResponse.data;
      const positionsArray = Array.isArray(positionsData) ? positionsData : [];
      
      setPmccDetail(pmccData);
      setConidTrades(trades_by_conid || {});
      setOpenPositions(positionsArray);
      
      // Generate LEAPS details from the trades (minimal frontend logic)
      const details = pmccData.leaps.map(leaps => {
        const conid = leaps.trade_conid;
        const trades = trades_by_conid[conid] || [];
        
        // Find opening trade by ID (leaps.trade is the trade ID from PMCCTrade)
        const openingTrade = trades.find(t => t.id === leaps.trade);
        
        if (!openingTrade) {
          console.warn(`Opening trade not found for LEAPS ${leaps.id}, CONID ${conid}`);
          return null;
        }
        
        // Find closing trade (SELL with same CONID and later date)
        const closingTrade = trades.find(t => 
          t.id !== openingTrade.id && 
          t.buy_sell === 'SELL' &&
          parseDate(t.trade_date) >= parseDate(openingTrade.trade_date)
        );
        
        const entryDate = parseDate(openingTrade.trade_date);
        const exitDate = closingTrade ? parseDate(closingTrade.trade_date) : null;
        const daysOpen = entryDate && exitDate ? Math.floor((exitDate - entryDate) / (1000 * 60 * 60 * 24)) : null;
        
        return {
          openingTrade,
          closingTrade,
          entryDate,
          exitDate,
          daysOpen,
          entryPrice: openingTrade.trade_price,
          exitPrice: closingTrade ? closingTrade.trade_price : null,
          pnl: closingTrade ? closingTrade.fifo_pnl_realized : (openingTrade.mtm_pnl || null),
          isClosed: !!closingTrade
        };
      });
      setLeapsDetails(details.filter(d => d !== null));
      
      setLoadingDetails(false);
      setView('detail');
      
      // Check if PMCC is open and has no open LEAPS
      console.log('handleViewDetail: PMCC is_closed:', pmccData.is_closed);
      console.log('handleViewDetail: PMCC leaps count:', pmccData.leaps?.length || 0);
      if (!pmccData.is_closed) {
        // If no LEAPS at all, show dialog
        if (!pmccData.leaps || pmccData.leaps.length === 0) {
          console.log('handleViewDetail: No LEAPS found, showing close dialog');
          setPmccToClose(pmccData);
          setShowClosePmccDialog(true);
        } else {
          const hasOpenLeaps = checkOpenLeaps(pmccData, trades_by_conid);
          console.log('handleViewDetail: hasOpenLeaps:', hasOpenLeaps);
          if (!hasOpenLeaps) {
            console.log('handleViewDetail: All LEAPS closed, showing close dialog');
            setPmccToClose(pmccData);
            setShowClosePmccDialog(true);
          }
        }
      }
    } catch (err) {
      setError('Error fetching PMCC details: ' + (err.response?.data?.detail || err.message));
      console.error(err);
      setLoadingDetails(false);
    }
  };

  const checkOpenLeaps = (pmccData, tradesByConid) => {
    // Check if there are any open LEAPS
    if (!pmccData.leaps || pmccData.leaps.length === 0) {
      console.log('checkOpenLeaps: No LEAPS found');
      return false;
    }

    console.log('checkOpenLeaps: Checking', pmccData.leaps.length, 'LEAPS');
    console.log('checkOpenLeaps: tradesByConid keys:', Object.keys(tradesByConid || {}));

    for (const leaps of pmccData.leaps) {
      const conid = leaps.trade_conid;
      const trades = tradesByConid[conid] || [];
      
      console.log(`checkOpenLeaps: Checking LEAPS ${leaps.id}, CONID ${conid}, trades count: ${trades.length}`);
      
      // Find opening trade
      const openingTrade = trades.find(t => t.id === leaps.trade);
      if (!openingTrade) {
        console.log(`checkOpenLeaps: Opening trade ${leaps.trade} not found for CONID ${conid}`);
        continue;
      }
      
      // Check if there's a closing trade (SELL with same CONID and later date)
      const closingTrade = trades.find(t => 
        t.id !== openingTrade.id && 
        t.buy_sell === 'SELL' &&
        parseDate(t.trade_date) >= parseDate(openingTrade.trade_date)
      );
      
      // If no closing trade found, this LEAPS is still open
      if (!closingTrade) {
        console.log(`checkOpenLeaps: LEAPS ${leaps.id} is still open (no closing trade found)`);
        return true;
      } else {
        console.log(`checkOpenLeaps: LEAPS ${leaps.id} is closed (closing trade found)`);
      }
    }
    
    // All LEAPS are closed
    console.log('checkOpenLeaps: All LEAPS are closed');
    return false;
  };

  const handleClosePmcc = async () => {
    if (!pmccToClose) return;
    
    try {
      setError(null);
      await axios.patch(`${API_URL}/portfolio/pmcc/${pmccToClose.id}/`, {
        is_closed: true
      });
      await fetchPMCCs();
      // Refresh current view if we're viewing/editing this PMCC
      if (pmccDetail && pmccDetail.id === pmccToClose.id) {
        await handleViewDetail({ id: pmccToClose.id });
      }
      if (selectedPmcc && selectedPmcc.id === pmccToClose.id) {
        const response = await axios.get(`${API_URL}/portfolio/pmcc/${pmccToClose.id}/`);
        setSelectedPmcc(response.data);
      }
      setShowClosePmccDialog(false);
      setPmccToClose(null);
    } catch (err) {
      setError('Error closing PMCC: ' + (err.response?.data?.detail || err.message));
      console.error(err);
    }
  };

  const toggleLeaps = (conid) => {
    // All trades are already loaded via all_trades endpoint, just toggle expansion
    setExpandedLeaps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(conid)) {
        newSet.delete(conid);
      } else {
        newSet.add(conid);
      }
      return newSet;
    });
  };

  // Group LEAPS by CONID
  const groupLeapsByConid = () => {
    if (!pmccDetail || !leapsDetails.length) return [];
    
    const grouped = {};
    pmccDetail.leaps.forEach((leaps, index) => {
      const detail = leapsDetails[index];
      if (!detail) return;
      
      const conid = leaps.trade_conid;
      if (!conid) {
        // If no CONID, treat as separate entry
        const key = `no_conid_${leaps.id}`;
        if (!grouped[key]) {
          grouped[key] = {
            conid: null,
            leaps: [leaps],
            details: [detail],
            isClosed: detail.isClosed,
            totalPnl: parseFloat(detail.pnl || 0),
            earliestEntryDate: detail.entryDate,
            latestExitDate: detail.exitDate,
            earliestEntryDateStr: detail.openingTrade?.trade_date,
            latestExitDateStr: detail.closingTrade?.trade_date
          };
        } else {
          // Add to existing group (shouldn't happen for no_conid, but handle it)
          grouped[key].leaps.push(leaps);
          grouped[key].details.push(detail);
          grouped[key].totalPnl += parseFloat(detail.pnl || 0);
          if (detail.entryDate && (!grouped[key].earliestEntryDate || detail.entryDate < grouped[key].earliestEntryDate)) {
            grouped[key].earliestEntryDate = detail.entryDate;
            grouped[key].earliestEntryDateStr = detail.openingTrade?.trade_date;
          }
          if (detail.exitDate && (!grouped[key].latestExitDate || detail.exitDate > grouped[key].latestExitDate)) {
            grouped[key].latestExitDate = detail.exitDate;
            grouped[key].latestExitDateStr = detail.closingTrade?.trade_date;
          }
          // If any is closed, mark as closed
          if (detail.isClosed) grouped[key].isClosed = true;
        }
      } else {
        if (!grouped[conid]) {
          grouped[conid] = {
            conid: conid,
            leaps: [leaps],
            details: [detail],
            isClosed: detail.isClosed,
            totalPnl: 0, // Will be calculated from actual trades
            earliestEntryDate: detail.entryDate,
            latestExitDate: detail.exitDate,
            earliestEntryDateStr: detail.openingTrade?.trade_date,
            latestExitDateStr: detail.closingTrade?.trade_date
          };
        } else {
          // Add to existing CONID group - aggregate all LEAPS with same CONID
          grouped[conid].leaps.push(leaps);
          grouped[conid].details.push(detail);
          // Track earliest entry date and latest exit date
          if (detail.entryDate && (!grouped[conid].earliestEntryDate || detail.entryDate < grouped[conid].earliestEntryDate)) {
            grouped[conid].earliestEntryDate = detail.entryDate;
            grouped[conid].earliestEntryDateStr = detail.openingTrade?.trade_date;
          }
          if (detail.exitDate && (!grouped[conid].latestExitDate || detail.exitDate > grouped[conid].latestExitDate)) {
            grouped[conid].latestExitDate = detail.exitDate;
            grouped[conid].latestExitDateStr = detail.closingTrade?.trade_date;
          }
          // If any is closed, mark as closed
          if (detail.isClosed) grouped[conid].isClosed = true;
        }
      }
    });
    
    // Note: totalPnl will be calculated in the render function based on actual trades
    // to ensure it matches what's shown when expanded
    
    return Object.values(grouped);
  };

  if (loading && pmccs.length === 0) {
    return <div className="pmcc-container">Loading...</div>;
  }

  if (view === 'create') {
    return (
      <div className="pmcc-container">
        <div className="pmcc-header">
          <h2>Create New PMCC</h2>
          <button onClick={() => {
            navigate('/pmcc');
            setView('list');
            setSelectedLeapsIds([]);
            setSelectedCoveredCallsIds([]);
            setDescription('');
            setTradePlan('');
            setPossibleCoveredCalls([]);
            setShowClosedLeaps(false);
          }}>Cancel</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="pmcc-form">
          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                <strong>LEAPS Selection</strong>
                <span 
                  className="info-icon" 
                  title="LEAPS are long-term call options (BUY to open) with at least 360 days until expiry. These are the long positions that 'cover' your short calls."
                  style={{ 
                    cursor: 'help', 
                    fontSize: '16px',
                    color: '#667eea'
                  }}
                >
                  ℹ️
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showClosedLeaps}
                  onChange={(e) => setShowClosedLeaps(e.target.checked)}
                />
                <span>Show closed LEAPS</span>
              </label>
            </div>
            {leapsTrades.length > 0 ? (
              <table className="pmcc-table" style={{ marginTop: '10px' }}>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>Select</th>
                    <th onClick={() => handleLeapsSort('symbol')} className="sortable-header">
                      Symbol {getLeapsSortIcon('symbol')}
                    </th>
                    <th onClick={() => handleLeapsSort('underlying')} className="sortable-header">
                      Underlying {getLeapsSortIcon('underlying')}
                    </th>
                    <th onClick={() => handleLeapsSort('trade_date')} className="sortable-header">
                      Trade Date {getLeapsSortIcon('trade_date')}
                    </th>
                    <th onClick={() => handleLeapsSort('expiry')} className="sortable-header">
                      Expiry Date {getLeapsSortIcon('expiry')}
                    </th>
                    <th onClick={() => handleLeapsSort('close_date')} className="sortable-header">
                      Closed Date {getLeapsSortIcon('close_date')}
                    </th>
                    <th onClick={() => handleLeapsSort('conid')} className="sortable-header">
                      CONID {getLeapsSortIcon('conid')}
                    </th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sortLeaps(leapsTrades).map(trade => {
                const isUsed = pmccs.some(pmcc => 
                  pmcc.leaps?.some(l => l.trade === trade.id)
                );
                const isSelected = selectedLeapsIds.includes(trade.id);
                
                // Get the underlying symbol of currently selected LEAPS (if any)
                const selectedLeaps = leapsTrades.filter(t => selectedLeapsIds.includes(t.id));
                const currentUnderlying = selectedLeaps.length > 0 
                  ? (selectedLeaps[0].underlying_symbol || (selectedLeaps[0].symbol ? selectedLeaps[0].symbol.split()[0] : null))
                  : null;
                const tradeUnderlying = trade.underlying_symbol || (trade.symbol ? trade.symbol.split()[0] : null);
                
                // Check if this trade has a different underlying than already selected ones
                const hasDifferentUnderlying = currentUnderlying && tradeUnderlying && 
                  currentUnderlying.toUpperCase() !== tradeUnderlying.toUpperCase();
                
                return (
                  <tr key={trade.id} className={isUsed && !isSelected ? 'disabled-row' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isUsed && !isSelected || (hasDifferentUnderlying && !isSelected)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // If selecting a trade with different underlying, deselect all others
                            if (hasDifferentUnderlying) {
                              setSelectedLeapsIds([trade.id]);
                            } else {
                              setSelectedLeapsIds([...selectedLeapsIds, trade.id]);
                            }
                          } else {
                            setSelectedLeapsIds(selectedLeapsIds.filter(id => id !== trade.id));
                          }
                        }}
                      />
                    </td>
                    <td><strong>{trade.symbol}</strong></td>
                    <td>{trade.underlying_symbol || 'N/A'}</td>
                    <td>{formatDate(trade.trade_date)}</td>
                    <td>{formatDate(trade.expiry)}</td>
                    <td>{trade.close_date ? formatDate(trade.close_date) : '-'}</td>
                    <td>{trade.conid || 'N/A'}</td>
                    <td>
                      {isUsed && !isSelected && <span className="used-badge">Already used</span>}
                      {hasDifferentUnderlying && !isSelected && <span className="used-badge">Different underlying</span>}
                    </td>
                  </tr>
                );
              })}
                </tbody>
              </table>
            ) : (
              <p className="no-data">No open LEAPS trades found. LEAPS must be long call options that are still open (not closed) with at least 360 days to expiry from the trade date.</p>
            )}
          </div>

          {selectedLeapsIds.length > 0 && (
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <strong>Covered Calls Selection (optional)</strong>
                <span 
                  className="info-icon" 
                  title="Covered calls are short call options (SELL to open) that you write against your LEAPS positions. You receive premium (credit) when opening these positions."
                  style={{ 
                    cursor: 'help', 
                    fontSize: '16px',
                    color: '#667eea'
                  }}
                >
                  ℹ️
                </span>
              </label>
              {possibleCoveredCalls.length > 0 ? (
                <table className="pmcc-table" style={{ marginTop: '10px' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>Select</th>
                      <th onClick={() => handleCoveredCallsSort('symbol')} className="sortable-header">
                        Symbol {getCoveredCallsSortIcon('symbol')}
                      </th>
                      <th onClick={() => handleCoveredCallsSort('trade_date')} className="sortable-header">
                        Trade Date {getCoveredCallsSortIcon('trade_date')}
                      </th>
                      <th onClick={() => handleCoveredCallsSort('expiry')} className="sortable-header">
                        Expiry Date {getCoveredCallsSortIcon('expiry')}
                      </th>
                      <th onClick={() => handleCoveredCallsSort('premium')} className="sortable-header">
                        Premium Received {getCoveredCallsSortIcon('premium')}
                      </th>
                      <th onClick={() => handleCoveredCallsSort('conid')} className="sortable-header">
                        CONID {getCoveredCallsSortIcon('conid')}
                      </th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortCoveredCalls(possibleCoveredCalls).map(trade => {
                      const isSelected = selectedCoveredCallsIds.includes(trade.id);
                      // Check if this covered call is already used in another PMCC
                      const isUsed = pmccs.some(pmcc => {
                        if (pmcc.trades && Array.isArray(pmcc.trades)) {
                          return pmcc.trades.some(pmccTrade => 
                            pmccTrade.trade_type === 'COVERED_CALL' && pmccTrade.trade === trade.id
                          );
                        }
                        return false;
                      });
                      
                      // For SELL trades, show proceeds (premium received) instead of trade_price
                      // trade_money is negative for SELL (money out), but we want to show it as positive (premium received)
                      const premiumReceived = trade.trade_money ? Math.abs(parseFloat(trade.trade_money)) : (trade.trade_price ? parseFloat(trade.trade_price) : 0);
                      return (
                        <tr key={trade.id} className={isUsed && !isSelected ? 'disabled-row' : ''}>
                          <td>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isUsed && !isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedCoveredCallsIds([...selectedCoveredCallsIds, trade.id]);
                                } else {
                                  setSelectedCoveredCallsIds(selectedCoveredCallsIds.filter(id => id !== trade.id));
                                }
                              }}
                            />
                          </td>
                          <td><strong>{trade.symbol}</strong></td>
                          <td>{formatDate(trade.trade_date)}</td>
                          <td>{formatDate(trade.expiry)}</td>
                          <td>{premiumReceived > 0 ? formatCurrency(premiumReceived) : formatCurrency(trade.trade_price || 0)}</td>
                          <td>{trade.conid || 'N/A'}</td>
                          <td>
                            {isUsed && !isSelected && <span className="used-badge">Already used</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p className="no-data">No covered calls found for the selected LEAPS. Covered calls must be CALL options with SELL to open, with the same underlying symbol and trade date between your LEAPS dates.</p>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="description">Description (optional):</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter an optional description for this PMCC"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="trade-plan">Trade Plan (optional):</label>
            <textarea
              id="trade-plan"
              value={tradePlan}
              onChange={(e) => setTradePlan(e.target.value)}
              placeholder="Enter an optional trade plan for this PMCC"
              rows={5}
            />
          </div>

          <div className="form-actions">
            <button
              onClick={handleCreate}
              disabled={selectedLeapsIds.length === 0}
            >
              Create PMCC
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'edit' && selectedPmcc) {
    return (
      <div className="pmcc-container">
        <div className="pmcc-header">
          <h2>Edit PMCC: {selectedPmcc.name}</h2>
          <button onClick={() => {
            navigate('/pmcc');
            setView('list');
            setSelectedPmcc(null);
          }}>Back to List</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="pmcc-details">
          <div className="pmcc-info">
            <p><strong>Underlying Symbol:</strong> {selectedPmcc.underlying_symbol}</p>
            <p><strong>Account:</strong> {selectedPmcc.account_display_name || selectedPmcc.account_id}</p>
            <p><strong>Created:</strong> {formatDate(selectedPmcc.created_at)}</p>
          </div>

          <div className="leaps-section">
            <div className="leaps-header">
              <h3>LEAPS ({selectedPmcc.leaps?.length || 0})</h3>
              <button onClick={openAddLeapsModal} className="btn-add-leaps">Add LEAPS</button>
            </div>

            {selectedPmcc.leaps && selectedPmcc.leaps.length > 0 ? (
              <table className="leaps-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Trade Date</th>
                    <th>Expiry</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedPmcc.leaps.map(leaps => (
                    <tr 
                      key={leaps.id}
                      className="leaps-row-clickable"
                      onClick={() => handleViewDetail(selectedPmcc)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{leaps.trade_symbol}</td>
                      <td>{formatDate(leaps.trade_date)}</td>
                      <td>{formatDate(leaps.trade_expiry)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button 
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to remove this LEAPS from the PMCC?')) {
                              try {
                                const currentLeapsIds = selectedPmcc.leaps.map(l => l.trade).filter(id => id !== leaps.trade);
                                await axios.patch(`${API_URL}/portfolio/pmcc/${selectedPmcc.id}/`, {
                                  leaps_trade_ids: currentLeapsIds
                                });
                                await fetchPMCCs();
                                const response = await axios.get(`${API_URL}/portfolio/pmcc/${selectedPmcc.id}/`);
                                setSelectedPmcc(response.data);
                              } catch (err) {
                                setError('Error removing LEAPS: ' + (err.response?.data?.detail || err.message));
                              }
                            }
                          }}
                          className="btn-remove"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-data">No LEAPS attached yet. Click "Add LEAPS" to add one.</p>
            )}
          </div>

          <div className="leaps-section" style={{ marginTop: '30px' }}>
            <div className="leaps-header">
              <h3>Covered Calls ({(selectedPmcc.trades || []).filter(t => t.trade_type === 'COVERED_CALL').length || 0})</h3>
              <button onClick={openAddCoveredCallsModal} className="btn-add-leaps">Add Covered Calls</button>
            </div>

            {(selectedPmcc.trades || []).filter(t => t.trade_type === 'COVERED_CALL').length > 0 ? (
              <table className="leaps-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Trade Date</th>
                    <th>Expiry</th>
                    <th>Premium Received</th>
                    <th>CONID</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedPmcc.trades || [])
                    .filter(t => t.trade_type === 'COVERED_CALL')
                    .map(coveredCall => {
                      return (
                        <tr key={coveredCall.id}>
                          <td>{coveredCall.trade_symbol || 'N/A'}</td>
                          <td>{formatDate(coveredCall.trade_date)}</td>
                          <td>{formatDate(coveredCall.trade_expiry)}</td>
                          <td>{coveredCall.trade_money ? `€ ${Math.abs(parseFloat(coveredCall.trade_money)).toFixed(2)}` : 'N/A'}</td>
                          <td>{coveredCall.trade_conid || 'N/A'}</td>
                          <td>
                            <button 
                              onClick={async () => {
                                if (window.confirm('Are you sure you want to remove this covered call from the PMCC?')) {
                                  try {
                                    const currentCoveredCallsIds = (selectedPmcc.trades || [])
                                      .filter(t => t.trade_type === 'COVERED_CALL' && t.trade !== coveredCall.trade)
                                      .map(t => t.trade);
                                    await axios.patch(`${API_URL}/portfolio/pmcc/${selectedPmcc.id}/`, {
                                      covered_calls_trade_ids: currentCoveredCallsIds
                                    });
                                    await fetchPMCCs();
                                    const response = await axios.get(`${API_URL}/portfolio/pmcc/${selectedPmcc.id}/`);
                                    setSelectedPmcc(response.data);
                                    await fetchPossibleCoveredCallsForEdit(response.data);
                                  } catch (err) {
                                    setError('Error removing covered call: ' + (err.response?.data?.detail || err.message));
                                  }
                                }
                              }}
                              className="btn-remove"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            ) : (
              <p className="no-data">No covered calls attached yet. Click "Add Covered Calls" to add one.</p>
            )}
          </div>
        </div>

        {/* Add LEAPS Modal */}
        {showAddLeapsModal && (
          <div className="modal-overlay" onClick={() => setShowAddLeapsModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Add LEAPS to {selectedPmcc.name}</h3>
                <button className="modal-close" onClick={() => setShowAddLeapsModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <p>Select LEAPS with underlying symbol: <strong>{selectedPmcc.underlying_symbol}</strong></p>
                <div className="leaps-list">
                  {availableLeapsForAdd.map(trade => (
                    <label key={trade.id} className="leaps-item">
                      <input
                        type="checkbox"
                        checked={selectedLeapsToAdd.includes(trade.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedLeapsToAdd([...selectedLeapsToAdd, trade.id]);
                          } else {
                            setSelectedLeapsToAdd(selectedLeapsToAdd.filter(id => id !== trade.id));
                          }
                        }}
                      />
                      <span>
                        {trade.symbol} - {formatDate(trade.trade_date)} - Expiry: {formatDate(trade.expiry)}
                      </span>
                    </label>
                  ))}
                </div>
                {availableLeapsForAdd.length === 0 && (
                  <p className="no-data">No available open LEAPS found for {selectedPmcc.underlying_symbol}. LEAPS must be long call options that are still open (not closed) with at least 360 days to expiry from the trade date.</p>
                )}
              </div>
              <div className="modal-footer">
                <button onClick={() => setShowAddLeapsModal(false)}>Cancel</button>
                <button 
                  onClick={handleAddLeaps}
                  disabled={selectedLeapsToAdd.length === 0}
                >
                  Add Selected LEAPS
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Covered Calls Modal */}
        {showAddCoveredCallsModal && (
          <div className="modal-overlay" onClick={() => setShowAddCoveredCallsModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Add Covered Calls to {selectedPmcc.name}</h3>
                <button className="modal-close" onClick={() => setShowAddCoveredCallsModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <p>Select covered calls with underlying symbol: <strong>{selectedPmcc.underlying_symbol}</strong></p>
                <div className="leaps-list">
                  {availableCoveredCallsForAdd.map(trade => {
                    // Check if this covered call is already used in another PMCC
                    const isUsed = pmccs.some(pmcc => {
                      if (pmcc.trades && Array.isArray(pmcc.trades)) {
                        return pmcc.trades.some(pmccTrade => 
                          pmccTrade.trade_type === 'COVERED_CALL' && pmccTrade.trade === trade.id
                        );
                      }
                      return false;
                    });
                    const isSelected = selectedCoveredCallsToAdd.includes(trade.id);
                    
                    return (
                      <label 
                        key={trade.id} 
                        className={`leaps-item ${isUsed && !isSelected ? 'disabled-row' : ''}`}
                        style={isUsed && !isSelected ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isUsed && !isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCoveredCallsToAdd([...selectedCoveredCallsToAdd, trade.id]);
                            } else {
                              setSelectedCoveredCallsToAdd(selectedCoveredCallsToAdd.filter(id => id !== trade.id));
                            }
                          }}
                        />
                        <span>
                          {trade.symbol} - {formatDate(trade.trade_date)} - Expiry: {formatDate(trade.expiry)} - Premium: € {parseFloat(trade.trade_money || 0).toFixed(2)}
                          {isUsed && !isSelected && <span className="used-badge">Already used</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {availableCoveredCallsForAdd.length === 0 && (
                  <p className="no-data">No available covered calls found for {selectedPmcc.underlying_symbol}. Covered calls must be short call options (SELL to open) that are within the date range of your LEAPS.</p>
                )}
              </div>
              <div className="modal-footer">
                <button onClick={() => setShowAddCoveredCallsModal(false)}>Cancel</button>
                <button 
                  onClick={handleAddCoveredCalls}
                  disabled={selectedCoveredCallsToAdd.length === 0}
                >
                  Add Selected Covered Calls
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (view === 'detail' && pmccDetail) {
    return (
      <div className="pmcc-container">
        <div className="pmcc-header">
          <h2>PMCC Details: {pmccDetail.name}</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            {!pmccDetail.is_closed && (
              <button 
                onClick={async () => {
                  if (window.confirm('Are you sure you want to close this PMCC position?')) {
                    try {
                      setError(null);
                      await axios.patch(`${API_URL}/portfolio/pmcc/${pmccDetail.id}/`, {
                        is_closed: true
                      });
                      await fetchPMCCs();
                      await handleViewDetail({ id: pmccDetail.id });
                    } catch (err) {
                      setError('Error closing PMCC: ' + (err.response?.data?.detail || err.message));
                      console.error(err);
                    }
                  }
                }}
                className="btn-delete"
              >
                Close PMCC
              </button>
            )}
            <button onClick={() => {
              navigate('/pmcc');
              setView('list');
              setPmccDetail(null);
              setLeapsDetails([]);
            }}>Back to List</button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="pmcc-details">
          <div className="pmcc-info">
            <h3>PMCC Information</h3>
            <p><strong>Name:</strong> {pmccDetail.name}</p>
            <p><strong>Underlying Symbol:</strong> {pmccDetail.underlying_symbol}</p>
            <p><strong>Account:</strong> {pmccDetail.account_display_name || pmccDetail.account_id}</p>
            <p><strong>Created:</strong> {formatDate(pmccDetail.created_at)}</p>
            <p><strong>Updated:</strong> {formatDate(pmccDetail.updated_at)}</p>
            {pmccDetail.description && (
              <div style={{ marginTop: '15px' }}>
                <p><strong>Description:</strong></p>
                <p style={{ whiteSpace: 'pre-wrap', marginLeft: '20px' }}>{pmccDetail.description}</p>
              </div>
            )}
            {pmccDetail.trade_plan && (
              <div style={{ marginTop: '15px' }}>
                <p><strong>Trade Plan:</strong></p>
                <p style={{ whiteSpace: 'pre-wrap', marginLeft: '20px' }}>{pmccDetail.trade_plan}</p>
              </div>
            )}
          </div>

          {(() => {
            // Calculate summary statistics
            const groupedLeaps = groupLeapsByConid();
            const closedLeaps = groupedLeaps.filter(g => g.isClosed);
            const openLeaps = groupedLeaps.filter(g => !g.isClosed);
            const totalLeapsPnl = groupedLeaps.reduce((sum, g) => {
              const { conid, isClosed } = g;
              if (isClosed && conid && conidTrades[conid]) {
                // For closed LEAPS: use realized P&L
                const closingTrades = conidTrades[conid].filter(t => {
                  const indicator = (t.open_close_indicator || '').toUpperCase();
                  return indicator === 'C' || indicator === 'CLOSE' || t.buy_sell === 'SELL';
                });
                const groupPnl = closingTrades.reduce((s, trade) => {
                  return s + parseFloat(trade.fifo_pnl_realized || 0);
                }, 0);
                return sum + groupPnl;
              } else if (!isClosed && conid && conidTrades[conid]) {
                // For open LEAPS: use mtm_pnl (unrealized P&L)
                const openingTrades = conidTrades[conid].filter(t => {
                  const indicator = (t.open_close_indicator || '').toUpperCase();
                  return (indicator === 'O' || indicator === 'OPEN') && t.buy_sell === 'BUY';
                });
                const groupPnl = openingTrades.reduce((s, trade) => {
                  return s + parseFloat(trade.mtm_pnl || 0);
                }, 0);
                return sum + groupPnl;
              } else if (isClosed && !conid) {
                // For closed LEAPS without CONID: use detail P&L
                const firstDetail = g.details.find(d => d.pnl !== null && d.pnl !== undefined);
                if (firstDetail) {
                  return sum + parseFloat(firstDetail.pnl || 0);
                }
              } else if (!isClosed && !conid) {
                // For open LEAPS without CONID: use mtm_pnl from details
                const firstDetail = g.details.find(d => d.openingTrade && d.openingTrade.mtm_pnl);
                if (firstDetail && firstDetail.openingTrade) {
                  return sum + parseFloat(firstDetail.openingTrade.mtm_pnl || 0);
                }
              }
              return sum;
            }, 0);
            
            // Calculate total covered calls P&L
            const coveredCalls = (pmccDetail.trades || []).filter(t => t.trade_type === 'COVERED_CALL');
            const groupedByConid = {};
            coveredCalls.forEach(cc => {
              const conid = cc.trade_conid || `no_conid_${cc.id}`;
              if (!groupedByConid[conid]) {
                groupedByConid[conid] = [];
              }
              groupedByConid[conid].push(cc);
            });
            
            const totalCoveredCallsPnl = Object.entries(groupedByConid).reduce((sum, [conid, calls]) => {
              const allTrades = conidTrades[conid] || [];
              const pmccUnderlyingSymbol = pmccDetail.underlying_symbol;
              
              const filteredTrades = allTrades.filter(t => {
                if (t.conid !== conid) return false;
                if (pmccUnderlyingSymbol) {
                  const tradeUnderlying = t.underlying_symbol || (t.symbol ? t.symbol.split()[0] : null);
                  if (tradeUnderlying && tradeUnderlying.toUpperCase() !== pmccUnderlyingSymbol.toUpperCase()) {
                    return false;
                  }
                }
                return true;
              });
              
              const closingTrades = filteredTrades.filter(t => {
                const indicator = (t.open_close_indicator || '').toUpperCase();
                return (indicator === 'C' || indicator === 'CLOSE') || (t.buy_sell === 'BUY' && indicator !== 'O' && indicator !== 'OPEN');
              });
              
              if (closingTrades.length > 0) {
                const groupPnl = closingTrades.reduce((s, trade) => {
                  return s + parseFloat(trade.fifo_pnl_realized || 0);
                }, 0);
                return sum + groupPnl;
              }
              return sum;
            }, 0);
            
            // Total P&L = LEAPS P&L + Covered Calls P&L
            const totalPnl = totalLeapsPnl + totalCoveredCallsPnl;
            
            const totalLeapsCount = groupedLeaps.length;
            const closedLeapsCount = closedLeaps.length;
            const openLeapsCount = openLeaps.length;
            
            return (
              <div className="pmcc-summary">
                <h3>PMCC Summary</h3>
                <div style={{ 
                  fontSize: '0.85rem', 
                  color: '#666', 
                  marginBottom: '15px', 
                  padding: '8px 12px', 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '4px',
                  borderLeft: '3px solid #ff8c00'
                }}>
                  <span style={{ fontWeight: '500' }}>P&L Colors: </span>
                  <span style={{ color: '#ff8c00' }}>Orange</span> = Unrealized P&L (open positions), 
                  <span style={{ color: '#28a745' }}> Green</span>/<span style={{ color: '#dc3545' }}>Red</span> = Realized P&L (closed positions)
                </div>
                <div className="summary-stats">
                  <div className="summary-stat">
                    <span className="stat-label">Total LEAPS:</span>
                    <span className="stat-value">{totalLeapsCount}</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-label">Closed LEAPS:</span>
                    <span className="stat-value">{closedLeapsCount}</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-label">Open LEAPS:</span>
                    <span className="stat-value">{openLeapsCount}</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-label">
                      Total P&L:
                      <span 
                        className="info-icon" 
                        title="Orange = Unrealized P&L (open positions). Green/Red = Realized P&L (closed positions)"
                        style={{ 
                          marginLeft: '5px',
                          fontSize: '12px',
                          color: '#666',
                          cursor: 'help'
                        }}
                      >
                        ℹ️
                      </span>
                    </span>
                    <span className={`stat-value ${(() => {
                      // Check if we have any open LEAPS or covered calls
                      const hasOpenLeaps = openLeaps.length > 0;
                      const hasOpenCoveredCalls = Object.entries(groupedByConid).some(([conid, calls]) => {
                        const allTrades = conidTrades[conid] || [];
                        const closingTrades = allTrades.filter(t => {
                          const indicator = (t.open_close_indicator || '').toUpperCase();
                          return (indicator === 'C' || indicator === 'CLOSE') || (t.buy_sell === 'BUY' && indicator !== 'O' && indicator !== 'OPEN');
                        });
                        return closingTrades.length === 0; // No closing trades means still open
                      });
                      
                      if (hasOpenLeaps || hasOpenCoveredCalls) {
                        // If there are open positions, show in orange (unrealized)
                        return 'pnl-unrealized';
                      } else {
                        // If all closed, show in green/red (realized)
                        return totalPnl >= 0 ? 'pnl-positive' : 'pnl-negative';
                      }
                    })()}`}>
                      {formatCurrency(totalPnl)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="leaps-details-section">
            <h3>LEAPS Details ({pmccDetail.leaps?.length || 0})</h3>
            {loadingDetails ? (
              <p>Loading LEAPS details...</p>
            ) : (() => {
              const groupedLeaps = groupLeapsByConid();
              if (groupedLeaps.length === 0) {
                return <p className="no-data">No LEAPS details available.</p>;
              }
              
              return (
                <table className="leaps-details-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Symbol</th>
                      <th>CONID</th>
                      <th>Entry Date</th>
                      <th>Entry Price</th>
                      <th>Current Price</th>
                      <th>Exit Date</th>
                      <th>Exit Price</th>
                      <th>Days Open</th>
                      <th>Status</th>
                      <th>P&L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedLeaps.map((group, groupIndex) => {
                      const { conid, leaps, details, isClosed, earliestEntryDateStr, latestExitDateStr } = group;
                      const groupKey = conid || `no_conid_${leaps[0].id}`;
                      const isExpanded = expandedLeaps.has(groupKey);
                      // Check if any detail has both opening and closing trades
                      const hasBothLegs = isClosed && details.some(d => d.closingTrade);
                      
                      // Calculate P&L from actual trades (same as shown when expanded)
                      // For closed: use realized P&L, for open: use unrealized P&L (mtm_pnl)
                      let totalPnl = 0;
                      if (isClosed && conid) {
                        if (conidTrades[conid]) {
                          // Use actual trades if loaded (preferred - most accurate)
                          const closingTrades = conidTrades[conid].filter(t => {
                            const indicator = (t.open_close_indicator || '').toUpperCase();
                            return indicator === 'C' || indicator === 'CLOSE' || t.buy_sell === 'SELL';
                          });
                          totalPnl = closingTrades.reduce((sum, trade) => {
                            return sum + parseFloat(trade.fifo_pnl_realized || 0);
                          }, 0);
                        } else {
                          // Fallback: use unique closing trades from details (avoid duplicates)
                          const uniqueClosingTrades = new Map();
                          details
                            .filter(d => d.closingTrade)
                            .forEach(d => {
                              const tradeId = d.closingTrade.id || d.closingTrade.trade_id;
                              if (tradeId && !uniqueClosingTrades.has(tradeId)) {
                                uniqueClosingTrades.set(tradeId, d.closingTrade);
                              }
                            });
                          
                          // Sum up P&L from all unique closing trades
                          totalPnl = Array.from(uniqueClosingTrades.values()).reduce((sum, trade) => {
                            return sum + parseFloat(trade.fifo_pnl_realized || 0);
                          }, 0);
                        }
                      } else if (!isClosed && conid && conidTrades[conid]) {
                        // For open LEAPS: use mtm_pnl (unrealized P&L) from opening trades
                        const openingTrades = conidTrades[conid].filter(t => {
                          const indicator = (t.open_close_indicator || '').toUpperCase();
                          return (indicator === 'O' || indicator === 'OPEN') && t.buy_sell === 'BUY';
                        });
                        totalPnl = openingTrades.reduce((sum, trade) => {
                          return sum + parseFloat(trade.mtm_pnl || 0);
                        }, 0);
                      } else if (isClosed && !conid) {
                        // For no CONID, use the detail P&L
                        const firstDetail = details.find(d => d.pnl !== null && d.pnl !== undefined);
                        if (firstDetail) {
                          totalPnl = parseFloat(firstDetail.pnl || 0);
                        }
                      } else if (!isClosed && !conid) {
                        // For open LEAPS without CONID, use mtm_pnl from details
                        const firstDetail = details.find(d => d.pnl !== null && d.pnl !== undefined);
                        if (firstDetail && firstDetail.openingTrade) {
                          totalPnl = parseFloat(firstDetail.openingTrade.mtm_pnl || 0);
                        }
                      }
                      
                      // Calculate entry price from actual opening trades (same as exit price calculation)
                      let avgEntryPrice = null;
                      if (conid && conidTrades[conid]) {
                        // Use actual trades if loaded (preferred - most accurate)
                        const openingTrades = conidTrades[conid].filter(t => {
                          const indicator = (t.open_close_indicator || '').toUpperCase();
                          return (indicator === 'O' || indicator === 'OPEN') && t.buy_sell === 'BUY';
                        });
                        if (openingTrades.length > 0) {
                          // Calculate weighted average by quantity, or simple average if no quantity
                          const totalQuantity = openingTrades.reduce((sum, t) => sum + Math.abs(parseFloat(t.quantity || 0)), 0);
                          if (totalQuantity > 0) {
                            avgEntryPrice = openingTrades.reduce((sum, t) => {
                              const quantity = Math.abs(parseFloat(t.quantity || 0));
                              return sum + (parseFloat(t.trade_price || 0) * quantity);
                            }, 0) / totalQuantity;
                          } else {
                            avgEntryPrice = openingTrades.reduce((sum, t) => sum + parseFloat(t.trade_price || 0), 0) / openingTrades.length;
                          }
                        }
                      } else {
                        // Fallback: use details if trades not loaded yet
                        avgEntryPrice = details.length > 0 
                          ? details.reduce((sum, d) => sum + parseFloat(d.entryPrice || 0), 0) / details.length 
                          : null;
                      }
                      
                      // Calculate exit price from actual closing trades (not from details)
                      // Use the latest exit price from the actual closing trades
                      let avgExitPrice = null;
                      if (isClosed && conid && conidTrades[conid]) {
                        const closingTrades = conidTrades[conid].filter(t => {
                          const indicator = (t.open_close_indicator || '').toUpperCase();
                          return indicator === 'C' || indicator === 'CLOSE' || (t.buy_sell === 'SELL' && indicator !== 'O' && indicator !== 'OPEN');
                        });
                        if (closingTrades.length > 0) {
                          // Calculate weighted average by quantity, or simple average if no quantity
                          const totalQuantity = closingTrades.reduce((sum, t) => sum + Math.abs(parseFloat(t.quantity || 0)), 0);
                          if (totalQuantity > 0) {
                            avgExitPrice = closingTrades.reduce((sum, t) => {
                              const quantity = Math.abs(parseFloat(t.quantity || 0));
                              return sum + (parseFloat(t.trade_price || 0) * quantity);
                            }, 0) / totalQuantity;
                          } else {
                            avgExitPrice = closingTrades.reduce((sum, t) => sum + parseFloat(t.trade_price || 0), 0) / closingTrades.length;
                          }
                        }
                      } else if (isClosed) {
                        // Fallback: use details if trades not loaded yet
                        const closedDetails = details.filter(d => d.closingTrade);
                        avgExitPrice = closedDetails.length > 0
                          ? closedDetails.reduce((sum, d) => sum + parseFloat(d.exitPrice || 0), 0) / closedDetails.length
                          : null;
                      }
                      
                      // Calculate total days open (average)
                      const daysOpenDetails = details.filter(d => d.daysOpen !== null);
                      const avgDaysOpen = daysOpenDetails.length > 0
                        ? Math.round(daysOpenDetails.reduce((sum, d) => sum + d.daysOpen, 0) / daysOpenDetails.length)
                        : null;
                      
                      // Get current price from open positions (only for open LEAPS)
                      let currentPrice = null;
                      if (!isClosed && conid && pmccDetail?.account_id) {
                        // Find open position matching this CONID and account
                        const matchingPosition = openPositions.find(pos => 
                          pos.conid === conid && 
                          (pos.account_id === pmccDetail.account_id || pos.account === pmccDetail.account) &&
                          parseFloat(pos.position || 0) > 0 // Long position
                        );
                        if (matchingPosition && matchingPosition.mark_price) {
                          currentPrice = parseFloat(matchingPosition.mark_price);
                        }
                      }
                      
                      return (
                        <React.Fragment key={groupKey}>
                          <tr className={isClosed ? 'leaps-closed-row' : 'leaps-open-row'}>
                            <td 
                              onClick={() => hasBothLegs && toggleLeaps(groupKey)}
                              style={{ cursor: hasBothLegs ? 'pointer' : 'default' }}
                              title={hasBothLegs ? (isExpanded ? 'Collapse' : 'Expand') : ''}
                            >
                              {hasBothLegs ? (isExpanded ? '▼' : '▶') : ''}
                            </td>
                            <td>{leaps[0].trade_symbol}</td>
                            <td>{conid || '-'}</td>
                            <td>{earliestEntryDateStr ? formatDate(earliestEntryDateStr) : '-'}</td>
                            <td>{avgEntryPrice ? formatCurrency(avgEntryPrice) : '-'}</td>
                            <td>{currentPrice !== null ? formatCurrency(currentPrice) : '-'}</td>
                            <td>{latestExitDateStr ? formatDate(latestExitDateStr) : '-'}</td>
                            <td>{avgExitPrice ? formatCurrency(avgExitPrice) : '-'}</td>
                            <td>{avgDaysOpen !== null ? `${avgDaysOpen} days` : '-'}</td>
                            <td>
                              <span className={isClosed ? 'status-closed' : 'status-open'}>
                                {isClosed ? 'Closed' : 'Open'}
                              </span>
                            </td>
                            <td className={(() => {
                              if (!totalPnl) return '';
                              const pnlValue = parseFloat(totalPnl);
                              if (isClosed) {
                                // For closed: green/red
                                return pnlValue < 0 ? 'pnl-negative' : 'pnl-positive';
                              } else {
                                // For open: orange
                                return 'pnl-unrealized';
                              }
                            })()}>
                              {totalPnl ? formatCurrency(totalPnl) : '-'}
                            </td>
                          </tr>
                          {hasBothLegs && isExpanded && (() => {
                            // Get all trades for this CONID
                            const tradesForConid = conidTrades[conid] || [];
                            
                            // Separate into opening (BUY/OPEN) and closing (SELL/CLOSE) trades
                            const openingTrades = tradesForConid.filter(t => {
                              const indicator = (t.open_close_indicator || '').toUpperCase();
                              return indicator === 'O' || indicator === 'OPEN' || t.buy_sell === 'BUY';
                            }).sort((a, b) => {
                              const dateA = parseDate(a.trade_date);
                              const dateB = parseDate(b.trade_date);
                              return dateA && dateB ? dateA - dateB : 0;
                            });
                            
                            const closingTrades = tradesForConid.filter(t => {
                              const indicator = (t.open_close_indicator || '').toUpperCase();
                              return indicator === 'C' || indicator === 'CLOSE' || t.buy_sell === 'SELL';
                            }).sort((a, b) => {
                              const dateA = parseDate(a.trade_date);
                              const dateB = parseDate(b.trade_date);
                              return dateA && dateB ? dateA - dateB : 0;
                            });
                            
                            return (
                              <>
                                {/* Show all opening BUY trades with this CONID */}
                                {openingTrades.map((trade, tradeIndex) => {
                                  // Get current price for this specific trade's CONID
                                  let tradeCurrentPrice = null;
                                  if (trade.conid && pmccDetail?.account_id) {
                                    const matchingPosition = openPositions.find(pos => 
                                      pos.conid === trade.conid && 
                                      (pos.account_id === pmccDetail.account_id || pos.account === pmccDetail.account) &&
                                      parseFloat(pos.position || 0) > 0
                                    );
                                    if (matchingPosition && matchingPosition.mark_price) {
                                      tradeCurrentPrice = parseFloat(matchingPosition.mark_price);
                                    }
                                  }
                                  
                                  return (
                                    <tr key={`opening-${conid}-${tradeIndex}`} className="leaps-component-row">
                                      <td></td>
                                      <td>{trade.symbol || trade.underlying_symbol || '-'}</td>
                                      <td>{trade.conid || '-'}</td>
                                      <td>{formatDate(trade.trade_date)}</td>
                                      <td>{formatCurrency(trade.trade_price)}</td>
                                      <td>{tradeCurrentPrice !== null ? formatCurrency(tradeCurrentPrice) : '-'}</td>
                                      <td>-</td>
                                      <td>-</td>
                                      <td>-</td>
                                      <td>
                                        <span className="status-open">BUY</span>
                                      </td>
                                      <td className={(() => {
                                        // For open trades: show mtm_pnl in orange
                                        const pnl = parseFloat(trade.mtm_pnl || 0);
                                        return pnl !== 0 ? 'pnl-unrealized' : '';
                                      })()}>
                                        {trade.mtm_pnl ? formatCurrency(trade.mtm_pnl) : '-'}
                                      </td>
                                    </tr>
                                  );
                                })}
                                {/* Show all closing SELL trades with this CONID */}
                                {closingTrades.map((trade, tradeIndex) => (
                                  <tr key={`closing-${conid}-${tradeIndex}`} className="leaps-component-row">
                                    <td></td>
                                    <td>{trade.symbol || trade.underlying_symbol || '-'}</td>
                                    <td>{trade.conid || '-'}</td>
                                    <td>-</td>
                                    <td>-</td>
                                    <td>-</td>
                                    <td>{formatDate(trade.trade_date)}</td>
                                    <td>{formatCurrency(trade.trade_price)}</td>
                                    <td>-</td>
                                    <td>
                                      <span className="status-closed">SELL</span>
                                    </td>
                                    <td className={trade.fifo_pnl_realized ? (parseFloat(trade.fifo_pnl_realized) < 0 ? 'pnl-negative' : 'pnl-positive') : ''}>
                                      {trade.fifo_pnl_realized ? formatCurrency(trade.fifo_pnl_realized) : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </>
                            );
                          })()}
                        </React.Fragment>
                      );
                    })}
                    {/* Total row for LEAPS */}
                    {(() => {
                      // Calculate total P&L from all LEAPS (closed: realized, open: unrealized)
                      const totalLeapsPnl = groupedLeaps.reduce((sum, group) => {
                        const { conid, isClosed } = group;
                        if (isClosed && conid && conidTrades[conid]) {
                          // For closed LEAPS: use realized P&L
                          const closingTrades = conidTrades[conid].filter(t => {
                            const indicator = (t.open_close_indicator || '').toUpperCase();
                            return indicator === 'C' || indicator === 'CLOSE' || t.buy_sell === 'SELL';
                          });
                          const groupPnl = closingTrades.reduce((s, trade) => {
                            return s + parseFloat(trade.fifo_pnl_realized || 0);
                          }, 0);
                          return sum + groupPnl;
                        } else if (!isClosed && conid && conidTrades[conid]) {
                          // For open LEAPS: use mtm_pnl (unrealized P&L)
                          const openingTrades = conidTrades[conid].filter(t => {
                            const indicator = (t.open_close_indicator || '').toUpperCase();
                            return (indicator === 'O' || indicator === 'OPEN') && t.buy_sell === 'BUY';
                          });
                          const groupPnl = openingTrades.reduce((s, trade) => {
                            return s + parseFloat(trade.mtm_pnl || 0);
                          }, 0);
                          return sum + groupPnl;
                        } else if (isClosed && !conid) {
                          // For closed LEAPS without CONID: use detail P&L
                          const firstDetail = group.details.find(d => d.pnl !== null && d.pnl !== undefined);
                          if (firstDetail) {
                            return sum + parseFloat(firstDetail.pnl || 0);
                          }
                        } else if (!isClosed && !conid) {
                          // For open LEAPS without CONID: use mtm_pnl from details
                          const firstDetail = group.details.find(d => d.openingTrade && d.openingTrade.mtm_pnl);
                          if (firstDetail && firstDetail.openingTrade) {
                            return sum + parseFloat(firstDetail.openingTrade.mtm_pnl || 0);
                          }
                        }
                        return sum;
                      }, 0);
                      
                      // Check if we have any open LEAPS
                      const hasOpenLeaps = groupedLeaps.some(g => !g.isClosed);
                      
                      return (
                        <tr className="total-row" style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                          <td colSpan="10" style={{ textAlign: 'left', paddingLeft: '20px' }}>Total LEAPS P&L:</td>
                          <td className={(() => {
                            if (hasOpenLeaps) {
                              // If there are open LEAPS, show in orange (unrealized)
                              return 'pnl-unrealized';
                            } else {
                              // If all closed, show in green/red (realized)
                              return totalLeapsPnl < 0 ? 'pnl-negative' : 'pnl-positive';
                            }
                          })()} style={{ textAlign: 'right' }}>
                            {formatCurrency(totalLeapsPnl)}
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              );
            })()}
          </div>

          {/* Covered Calls Section */}
          <div className="leaps-details-section" style={{ marginTop: '40px' }}>
            <h3>Covered Calls ({(pmccDetail.trades || []).filter(t => t.trade_type === 'COVERED_CALL').length || 0})</h3>
            {(pmccDetail.trades || []).filter(t => t.trade_type === 'COVERED_CALL').length > 0 ? (
              <table className="leaps-details-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Symbol</th>
                    <th>CONID</th>
                    <th>Entry Date</th>
                    <th>Entry Price</th>
                    <th>Exit Date</th>
                    <th>Exit Price</th>
                    <th>Status</th>
                    <th>P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Group covered calls by CONID
                    const coveredCalls = (pmccDetail.trades || []).filter(t => t.trade_type === 'COVERED_CALL');
                    const groupedByConid = {};
                    
                    coveredCalls.forEach(cc => {
                      const conid = cc.trade_conid || `no_conid_${cc.id}`;
                      if (!groupedByConid[conid]) {
                        groupedByConid[conid] = [];
                      }
                      groupedByConid[conid].push(cc);
                    });
                    
                    // Calculate total P&L from all closed covered calls first
                    const totalCoveredCallsPnl = Object.entries(groupedByConid).reduce((sum, [conid, calls]) => {
                      const allTrades = conidTrades[conid] || [];
                      const pmccUnderlyingSymbol = pmccDetail.underlying_symbol;
                      
                      // Filter trades to ensure they match the exact CONID, underlying symbol, AND covered call definition
                      // Covered call definition:
                      // - Opening: open_close_indicator = "O"/"OPEN", buy_sell = "SELL", put_call = "C"
                      // - Closing: open_close_indicator = "C"/"CLOSE", buy_sell = "BUY", put_call = "C"
                      const filteredTrades = allTrades.filter(t => {
                        if (t.conid !== conid) return false;
                        if (t.put_call !== 'C') return false; // Must be a CALL
                        if (pmccUnderlyingSymbol) {
                          const tradeUnderlying = t.underlying_symbol || (t.symbol ? t.symbol.split()[0] : null);
                          if (tradeUnderlying && tradeUnderlying.toUpperCase() !== pmccUnderlyingSymbol.toUpperCase()) {
                            return false;
                          }
                        }
                        // Must match covered call definition
                        const indicator = (t.open_close_indicator || '').toUpperCase();
                        const isOpening = (indicator === 'O' || indicator === 'OPEN') && t.buy_sell === 'SELL';
                        const isClosing = (indicator === 'C' || indicator === 'CLOSE') && t.buy_sell === 'BUY';
                        return isOpening || isClosing;
                      });
                      
                      // Closing trades are already filtered above (isClosing)
                      const closingTrades = filteredTrades.filter(t => {
                        const indicator = (t.open_close_indicator || '').toUpperCase();
                        return (indicator === 'C' || indicator === 'CLOSE') && t.buy_sell === 'BUY';
                      });
                      
                      if (closingTrades.length > 0) {
                        const groupPnl = closingTrades.reduce((s, trade) => {
                          return s + parseFloat(trade.fifo_pnl_realized || 0);
                        }, 0);
                        return sum + groupPnl;
                      }
                      return sum;
                    }, 0);
                    
                    // Sort covered calls by entry date (newest first)
                    const sortedCoveredCalls = Object.entries(groupedByConid).sort(([conidA, callsA], [conidB, callsB]) => {
                      const allTradesA = conidTrades[conidA] || [];
                      const allTradesB = conidTrades[conidB] || [];
                      
                      // Get opening trades for both
                      const openingTradesA = allTradesA.filter(t => {
                        if (t.conid !== conidA || t.put_call !== 'C') return false;
                        const indicator = (t.open_close_indicator || '').toUpperCase();
                        return (indicator === 'O' || indicator === 'OPEN') && t.buy_sell === 'SELL';
                      });
                      const openingTradesB = allTradesB.filter(t => {
                        if (t.conid !== conidB || t.put_call !== 'C') return false;
                        const indicator = (t.open_close_indicator || '').toUpperCase();
                        return (indicator === 'O' || indicator === 'OPEN') && t.buy_sell === 'SELL';
                      });
                      
                      // Get entry dates (trade_date of first opening trade)
                      let entryDateA = openingTradesA.length > 0 ? parseDate(openingTradesA[0].trade_date) : null;
                      let entryDateB = openingTradesB.length > 0 ? parseDate(openingTradesB[0].trade_date) : null;
                      
                      // Fallback to firstCall trade_date if no opening trades found
                      if (!entryDateA && callsA[0]?.trade_date) {
                        entryDateA = parseDate(callsA[0].trade_date);
                      }
                      if (!entryDateB && callsB[0]?.trade_date) {
                        entryDateB = parseDate(callsB[0].trade_date);
                      }
                      
                      // Sort: newest first (descending)
                      if (!entryDateA && !entryDateB) return 0;
                      if (!entryDateA) return 1;
                      if (!entryDateB) return -1;
                      return entryDateB - entryDateA; // Descending order (newest first)
                    });
                    
                    return (
                      <>
                        {sortedCoveredCalls.map(([conid, calls]) => {
                      const firstCall = calls[0];
                      const allTrades = conidTrades[conid] || [];
                      const pmccUnderlyingSymbol = pmccDetail.underlying_symbol;
                      
                      // Filter trades to ensure they match the exact CONID, underlying symbol, AND covered call definition
                      // Covered call definition:
                      // - Opening: open_close_indicator = "O"/"OPEN", buy_sell = "SELL", put_call = "C"
                      // - Closing: open_close_indicator = "C"/"CLOSE", buy_sell = "BUY", put_call = "C"
                      const filteredTrades = allTrades.filter(t => {
                        // Must have exact CONID match
                        if (t.conid !== conid) return false;
                        
                        // Must be a CALL option
                        if (t.put_call !== 'C') return false;
                        
                        // Must have matching underlying symbol (if PMCC has one)
                        if (pmccUnderlyingSymbol) {
                          const tradeUnderlying = t.underlying_symbol || (t.symbol ? t.symbol.split()[0] : null);
                          if (tradeUnderlying && tradeUnderlying.toUpperCase() !== pmccUnderlyingSymbol.toUpperCase()) {
                            return false;
                          }
                        }
                        
                        // Must match covered call definition
                        const indicator = (t.open_close_indicator || '').toUpperCase();
                        const isOpening = (indicator === 'O' || indicator === 'OPEN') && t.buy_sell === 'SELL';
                        const isClosing = (indicator === 'C' || indicator === 'CLOSE') && t.buy_sell === 'BUY';
                        
                        // Only include if it's a valid covered call trade (opening SELL or closing BUY)
                        return isOpening || isClosing;
                      });
                      
                      // Find opening trade (SELL)
                      const openingTrades = filteredTrades.filter(t => {
                        const indicator = (t.open_close_indicator || '').toUpperCase();
                        return (indicator === 'O' || indicator === 'OPEN') && t.buy_sell === 'SELL';
                      });
                      
                      // Find closing trades (BUY)
                      const closingTrades = filteredTrades.filter(t => {
                        const indicator = (t.open_close_indicator || '').toUpperCase();
                        return (indicator === 'C' || indicator === 'CLOSE') || (t.buy_sell === 'BUY' && indicator !== 'O' && indicator !== 'OPEN');
                      });
                      
                      const isClosed = closingTrades.length > 0;
                      const totalPnl = closingTrades.reduce((sum, trade) => {
                        return sum + parseFloat(trade.fifo_pnl_realized || 0);
                      }, 0);
                      
                      return (
                        <React.Fragment key={conid}>
                          {/* Summary row */}
                          <tr 
                            className="leaps-summary-row"
                            style={{ cursor: 'pointer', backgroundColor: isClosed ? '#f0f0f0' : '#fff' }}
                            onClick={() => {
                              const newExpanded = new Set(expandedLeaps);
                              if (newExpanded.has(`cc_${conid}`)) {
                                newExpanded.delete(`cc_${conid}`);
                              } else {
                                newExpanded.add(`cc_${conid}`);
                              }
                              setExpandedLeaps(newExpanded);
                            }}
                          >
                            <td>{expandedLeaps.has(`cc_${conid}`) ? '▼' : '▶'}</td>
                            <td>{firstCall.trade_symbol || 'N/A'}</td>
                            <td>{conid !== `no_conid_${firstCall.id}` ? conid : 'N/A'}</td>
                            <td>{openingTrades.length > 0 ? formatDate(openingTrades[0].trade_date) : formatDate(firstCall.trade_date)}</td>
                            <td>
                              {(() => {
                                // Use actual trade price from opening trades if available, otherwise from firstCall
                                if (openingTrades.length > 0) {
                                  // Calculate average entry price (weighted by quantity if available)
                                  const totalQuantity = openingTrades.reduce((sum, t) => sum + Math.abs(parseFloat(t.quantity || 0)), 0);
                                  if (totalQuantity > 0) {
                                    const avgPrice = openingTrades.reduce((sum, t) => {
                                      const quantity = Math.abs(parseFloat(t.quantity || 0));
                                      return sum + (parseFloat(t.trade_price || 0) * quantity);
                                    }, 0) / totalQuantity;
                                    return formatCurrency(avgPrice);
                                  } else {
                                    const avgPrice = openingTrades.reduce((sum, t) => sum + parseFloat(t.trade_price || 0), 0) / openingTrades.length;
                                    return formatCurrency(avgPrice);
                                  }
                                } else if (firstCall.trade_price) {
                                  return formatCurrency(firstCall.trade_price);
                                }
                                return 'N/A';
                              })()}
                            </td>
                            <td>{isClosed && closingTrades.length > 0 ? formatDate(closingTrades[closingTrades.length - 1].trade_date) : '-'}</td>
                            <td>{isClosed && closingTrades.length > 0 ? formatCurrency(closingTrades[closingTrades.length - 1].trade_price) : '-'}</td>
                            <td>
                              <span className={isClosed ? 'status-closed' : 'status-open'}>
                                {isClosed ? 'Closed' : 'Open'}
                              </span>
                            </td>
                            <td className={isClosed && totalPnl ? (parseFloat(totalPnl) < 0 ? 'pnl-negative' : 'pnl-positive') : ''}>
                              {isClosed ? formatCurrency(totalPnl) : '-'}
                            </td>
                          </tr>
                          
                          {/* Expanded details */}
                          {expandedLeaps.has(`cc_${conid}`) && (
                            <>
                              {openingTrades.map((trade, idx) => (
                                <tr key={`cc-opening-${conid}-${idx}`} className="leaps-component-row">
                                  <td></td>
                                  <td>{trade.symbol || trade.underlying_symbol || '-'}</td>
                                  <td>{trade.conid || '-'}</td>
                                  <td>{formatDate(trade.trade_date)}</td>
                                  <td>{formatCurrency(trade.trade_price || 0)}</td>
                                  <td>-</td>
                                  <td>-</td>
                                  <td><span className="status-open">SELL</span></td>
                                  <td>-</td>
                                </tr>
                              ))}
                              {closingTrades.map((trade, idx) => (
                                <tr key={`cc-closing-${conid}-${idx}`} className="leaps-component-row">
                                  <td></td>
                                  <td>{trade.symbol || trade.underlying_symbol || '-'}</td>
                                  <td>{trade.conid || '-'}</td>
                                  <td>{formatDate(trade.trade_date)}</td>
                                  <td>-</td>
                                  <td>{formatDate(trade.trade_date)}</td>
                                  <td>{formatCurrency(trade.trade_price)}</td>
                                  <td><span className="status-closed">BUY</span></td>
                                  <td className={trade.fifo_pnl_realized ? (parseFloat(trade.fifo_pnl_realized) < 0 ? 'pnl-negative' : 'pnl-positive') : ''}>
                                    {trade.fifo_pnl_realized ? formatCurrency(trade.fifo_pnl_realized) : '-'}
                                  </td>
                                </tr>
                              ))}
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {/* Total row for Covered Calls */}
                    <tr className="total-row" style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      <td colSpan="8" style={{ textAlign: 'left', paddingLeft: '20px' }}>Total Covered Calls P&L:</td>
                      <td className={totalCoveredCallsPnl < 0 ? 'pnl-negative' : 'pnl-positive'} style={{ textAlign: 'right' }}>
                        {formatCurrency(totalCoveredCallsPnl)}
                      </td>
                    </tr>
                      </>
                    );
                  })()}
                </tbody>
              </table>
            ) : (
              <p className="no-data">No covered calls attached to this PMCC.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pmcc-container">
      <div className="pmcc-header">
        <h2>PMCC Strategies</h2>
        <button onClick={() => setView('create')}>Create New PMCC</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {pmccs.length === 0 ? (
        <div className="no-data">No PMCC strategies found. Create one to get started.</div>
      ) : (
        <>
          {/* Open PMCCs Table */}
          {(() => {
            const openPmccs = pmccs.filter(pmcc => !pmcc.is_closed);
            if (openPmccs.length === 0) return null;
            
            return (
              <div style={{ marginBottom: '40px' }}>
                <h3 style={{ marginBottom: '15px', color: '#2e7d32' }}>Open PMCCs ({openPmccs.length})</h3>
                <table className="pmcc-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Company</th>
                      <th>Created</th>
                      <th>Account</th>
                      <th>LEAPS Count</th>
                      <th>P&L Unrealized</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openPmccs.map(pmcc => (
                      <tr 
                        key={pmcc.id}
                        className="pmcc-row-clickable"
                        onClick={() => handleViewDetail(pmcc)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{pmcc.name}</td>
                        <td>{pmcc.company_name || pmcc.underlying_symbol}</td>
                        <td>{formatDate(pmcc.created_at)}</td>
                        <td>{pmcc.account_display_name || pmcc.account_id}</td>
                        <td>{pmcc.leaps?.length || 0}</td>
                        <td className={(() => {
                          if (!pmcc.total_pnl) return '';
                          const pnl = parseFloat(pmcc.total_pnl || 0);
                          return pnl >= 0 ? 'pnl-positive' : 'pnl-negative';
                        })()}>
                          {pmcc.total_pnl ? formatCurrency(pmcc.total_pnl) : '-'}
                        </td>
                        <td>
                          <span className="status-open">Open</span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => handleEdit(pmcc)} className="btn-edit">Edit</button>
                          <button 
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (window.confirm('Are you sure you want to close this PMCC position?')) {
                                try {
                                  setError(null);
                                  await axios.patch(`${API_URL}/portfolio/pmcc/${pmcc.id}/`, {
                                    is_closed: true
                                  });
                                  await fetchPMCCs();
                                } catch (err) {
                                  setError('Error closing PMCC: ' + (err.response?.data?.detail || err.message));
                                  console.error(err);
                                }
                              }
                            }}
                            className="btn-close"
                          >
                            Close
                          </button>
                          <button onClick={() => handleDelete(pmcc)} className="btn-delete">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* Closed PMCCs Table */}
          {(() => {
            const closedPmccs = pmccs.filter(pmcc => pmcc.is_closed);
            if (closedPmccs.length === 0) return null;
            
            return (
              <div>
                <h3 style={{ marginBottom: '15px', color: '#d32f2f' }}>Closed PMCCs ({closedPmccs.length})</h3>
                <table className="pmcc-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Company</th>
                      <th>Created</th>
                      <th>Account</th>
                      <th>LEAPS Count</th>
                      <th>P&L Realized</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closedPmccs.map(pmcc => (
                      <tr 
                        key={pmcc.id}
                        className="pmcc-row-clickable pmcc-closed"
                        onClick={() => handleViewDetail(pmcc)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{pmcc.name}</td>
                        <td>{pmcc.company_name || pmcc.underlying_symbol}</td>
                        <td>{formatDate(pmcc.created_at)}</td>
                        <td>{pmcc.account_display_name || pmcc.account_id}</td>
                        <td>{pmcc.leaps?.length || 0}</td>
                        <td className={(() => {
                          if (!pmcc.total_pnl) return '';
                          const pnl = parseFloat(pmcc.total_pnl || 0);
                          return pnl >= 0 ? 'pnl-positive' : 'pnl-negative';
                        })()}>
                          {pmcc.total_pnl ? formatCurrency(pmcc.total_pnl) : '-'}
                        </td>
                        <td>
                          <span className="status-closed">Closed</span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => handleDelete(pmcc)} className="btn-delete">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </>
      )}

      {/* Close PMCC Dialog */}
      {showClosePmccDialog && pmccToClose && (
        <div className="modal-overlay" onClick={() => {
          setShowClosePmccDialog(false);
          setPmccToClose(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Close PMCC Position</h3>
              <button className="modal-close" onClick={() => {
                setShowClosePmccDialog(false);
                setPmccToClose(null);
              }}>×</button>
            </div>
            <div className="modal-body">
              <p>There are no more LEAPS open.</p>
              <p>Do you like to close the PMCC position?</p>
            </div>
            <div className="modal-footer">
              <button onClick={() => {
                setShowClosePmccDialog(false);
                setPmccToClose(null);
              }}>No</button>
              <button onClick={handleClosePmcc} className="btn-delete">Yes, Close PMCC</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PMCC;

